import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiX, FiCheck, FiChevronDown, FiArrowRight } from 'react-icons/fi';

const THEMES = {
  intro:    { bg: 'linear-gradient(145deg, #1A1A2E 0%, #2D2D50 40%, #3A3A6A 100%)', acc: '#6C63FF', acc2: '#8B83FF', glow: 'rgba(108,99,255,0.12)', card: 'rgba(20,20,40,0.55)', cardBd: 'rgba(108,99,255,0.35)' },
  capture1: { bg: 'linear-gradient(145deg, #0E2A2E 0%, #143D3D 40%, #1A5252 100%)', acc: '#2DD4BF', acc2: '#6BCB77', glow: 'rgba(45,212,191,0.12)', card: 'rgba(10,30,30,0.55)', cardBd: 'rgba(45,212,191,0.35)' },
  capture2: { bg: 'linear-gradient(145deg, #1A1A2E 0%, #2E1A48 40%, #3D1A60 100%)', acc: '#C084FC', acc2: '#FF6584', glow: 'rgba(192,132,252,0.12)', card: 'rgba(25,15,40,0.55)', cardBd: 'rgba(192,132,252,0.35)' },
  capture3: { bg: 'linear-gradient(145deg, #1A1A2E 0%, #1E2E4A 40%, #243D66 100%)', acc: '#4D96FF', acc2: '#6C63FF', glow: 'rgba(77,150,255,0.12)', card: 'rgba(15,20,40,0.55)', cardBd: 'rgba(77,150,255,0.35)' },
  capture4: { bg: 'linear-gradient(145deg, #1A1A2E 0%, #2A1A50 40%, #3A2070 100%)', acc: '#8B83FF', acc2: '#C084FC', glow: 'rgba(139,131,255,0.12)', card: 'rgba(20,15,45,0.55)', cardBd: 'rgba(139,131,255,0.35)' },
  capture5: { bg: 'linear-gradient(145deg, #1A1A2E 0%, #1E2848 40%, #243460 100%)', acc: '#6C63FF', acc2: '#4D96FF', glow: 'rgba(108,99,255,0.12)', card: 'rgba(15,20,35,0.55)', cardBd: 'rgba(108,99,255,0.35)' },
  closing:  { bg: 'linear-gradient(145deg, #1A1A2E 0%, #2E2418 40%, #3D3020 100%)', acc: '#FFD93D', acc2: '#FF8B3D', glow: 'rgba(255,217,61,0.12)', card: 'rgba(30,20,10,0.55)', cardBd: 'rgba(255,217,61,0.35)' },
};

const THEMES_GOLD = {
  intro:    { bg: 'linear-gradient(145deg, #1A1A2E 0%, #2E2418 40%, #3D3020 100%)', acc: '#FFD93D', acc2: '#FFAB40', glow: 'rgba(255,217,61,0.12)', card: 'rgba(30,20,10,0.55)', cardBd: 'rgba(255,217,61,0.30)' },
  capture1: { bg: 'linear-gradient(145deg, #1A1E0E 0%, #2E3510 40%, #3D4A14 100%)', acc: '#FFD93D', acc2: '#C8E64A', glow: 'rgba(255,217,61,0.12)', card: 'rgba(25,25,8,0.55)', cardBd: 'rgba(255,217,61,0.30)' },
  capture2: { bg: 'linear-gradient(145deg, #1A1A2E 0%, #352418 40%, #4A3020 100%)', acc: '#FFAB40', acc2: '#FF6B6B', glow: 'rgba(255,171,64,0.12)', card: 'rgba(30,18,10,0.55)', cardBd: 'rgba(255,171,64,0.30)' },
  capture3: { bg: 'linear-gradient(145deg, #1A1A2E 0%, #2E2818 40%, #3D3820 100%)', acc: '#FFD93D', acc2: '#FF8B3D', glow: 'rgba(255,217,61,0.12)', card: 'rgba(28,22,10,0.55)', cardBd: 'rgba(255,217,61,0.30)' },
  capture4: { bg: 'linear-gradient(145deg, #1A1A2E 0%, #2A2018 40%, #3A2E20 100%)', acc: '#FFAB40', acc2: '#FFD93D', glow: 'rgba(255,171,64,0.12)', card: 'rgba(25,18,10,0.55)', cardBd: 'rgba(255,171,64,0.30)' },
  capture5: { bg: 'linear-gradient(145deg, #1A1A2E 0%, #28241A 40%, #38321E 100%)', acc: '#FFD93D', acc2: '#FFAB40', glow: 'rgba(255,217,61,0.12)', card: 'rgba(26,20,10,0.55)', cardBd: 'rgba(255,217,61,0.30)' },
  closing:  { bg: 'linear-gradient(145deg, #1A1A2E 0%, #2E2418 40%, #3D3020 100%)', acc: '#FFD93D', acc2: '#FF8B3D', glow: 'rgba(255,217,61,0.12)', card: 'rgba(30,20,10,0.55)', cardBd: 'rgba(255,217,61,0.35)' },
};

// ─── Cast (characters from the lesson videos) ────────────────────────────────
// Cutouts live in /characters/cast/<key>.png (transparent PNG, head at top).
const CAST = {
  trace:  { name: 'Trace',       role: 'Host',                  side: 'hero' },
  mina:   { name: 'Mina',        role: 'Best-practices expert', side: 'hero' },
  flash:  { name: 'Dr. Flash',   role: 'Weapons developer',     side: 'hero' },
  prince: { name: 'Prince',      role: 'Lab assistant',         side: 'hero' },
  juss:   { name: 'Juss',        role: 'Threat hunter',         side: 'hero' },
  cass:   { name: 'Cass',        role: 'Leader of the hunters', side: 'hero' },
  profz:  { name: 'Professor Z', role: 'New technologies',      side: 'hero' },
  tim:    { name: 'Tim',         role: 'Student',               side: 'neutral' },
  ben:    { name: 'Ben',         role: 'Student',               side: 'neutral' },
  jason:  { name: 'Jason',       role: 'Leader of the villains', side: 'villain' },
  jackie: { name: 'Jackie',      role: 'Villain',               side: 'villain' },
};
const castImg = (who) => `/characters/cast/${who}.png`;

// ─── C.O.R.E. brand (core-aiowl.org): navy + gold + ivory, Outfit ────────────
export const BRAND = {
  navy900: '#07142d', navy800: '#0b1a3a', navy700: '#12213c', navy600: '#1b2d52',
  gold: '#cd9b50', goldLight: '#e2b878', goldPale: '#f3dcb4', ivory: '#f9f0e7',
  font: "'Outfit', 'Nunito', system-ui, -apple-system, sans-serif",
  serif: "'Cormorant Garamond', Georgia, serif",
  owl: '/brand/core-owl.png',
};
// Theme used by the V2 (scene-based) lessons: one brand palette for every chapter.
const THEME_CORE = { bg: `linear-gradient(145deg, ${BRAND.navy900} 0%, ${BRAND.navy800} 50%, ${BRAND.navy600} 100%)`, acc: BRAND.goldLight, acc2: BRAND.gold, glow: 'rgba(205,155,80,0.14)', card: 'rgba(7,20,45,0.48)', cardBd: 'rgba(226,184,120,0.32)', ink: BRAND.navy900 };

const SAY_STYLE = {
  hero:    { bd: BRAND.goldLight, bg: 'rgba(7,20,45,0.9)', name: BRAND.goldLight, text: BRAND.ivory },
  villain: { bd: '#FF6B6B', bg: 'rgba(48,16,22,0.92)', name: '#ff9b9b', text: '#ffe6e6' },
  neutral: { bd: '#5CC8FF', bg: 'rgba(8,30,52,0.92)', name: '#8ad8ff', text: '#eaf7ff' },
};

// ─── Character voices (Web Speech API) ───────────────────────────────────────
const VOICE = {
  trace: { pitch: 1.0, rate: 1.0 }, mina: { pitch: 1.2, rate: 1.0 }, flash: { pitch: 1.1, rate: 1.08 }, prince: { pitch: 1.0, rate: 1.0 },
  juss: { pitch: 0.82, rate: 0.98 }, cass: { pitch: 0.9, rate: 0.98 }, profz: { pitch: 0.88, rate: 0.92 },
  tim: { pitch: 1.35, rate: 1.08 }, ben: { pitch: 1.3, rate: 1.1 }, jason: { pitch: 0.75, rate: 0.95 }, jackie: { pitch: 1.15, rate: 1.0 },
};
let voiceCache = null;
function pickVoice(who) {
  const synth = window.speechSynthesis;
  if (!synth) return null;
  if (!voiceCache || !voiceCache.length) voiceCache = synth.getVoices();
  const en = voiceCache.filter(v => /^en(-|_)?(US|GB|AU|CA)?/i.test(v.lang));
  if (!en.length) return null;
  const female = ['mina', 'jackie'].includes(who);
  const kid = ['tim', 'ben'].includes(who);
  const byName = (re) => en.find(v => re.test(v.name));
  if (female) return byName(/samantha|victoria|karen|moira|zira|female|aria|jenny/i) || en[0];
  if (kid) return byName(/samantha|aria|jenny|female/i) || en[0];
  return byName(/daniel|alex|david|guy|male|fred|tom/i) || en[0];
}
export function speak(text, who) {
  const synth = window.speechSynthesis;
  if (!synth || !text) return false;
  synth.cancel();
  const u = new SpeechSynthesisUtterance(text);
  const v = VOICE[who] || { pitch: 1, rate: 1 };
  u.pitch = v.pitch; u.rate = v.rate; u.lang = 'en-US';
  const voice = pickVoice(who);
  if (voice) u.voice = voice;
  synth.speak(u);
  return true;
}
if (typeof window !== 'undefined' && window.speechSynthesis) {
  window.speechSynthesis.onvoiceschanged = () => { voiceCache = window.speechSynthesis.getVoices(); };
}

/** Speech callouts: slide.say = [{ who: 'mina', text: '...', role?: 'hero'|'villain'|'neutral' }] */
function Say({ items, compact }) {
  const [speaking, setSpeaking] = useState(null);
  useEffect(() => () => { if (window.speechSynthesis) window.speechSynthesis.cancel(); }, []);
  if (!items || !items.length) return null;
  const canSpeak = typeof window !== 'undefined' && !!window.speechSynthesis;
  const onSpeak = (i, s) => {
    if (speaking === i) { window.speechSynthesis.cancel(); setSpeaking(null); return; }
    if (speak(s.text, s.who)) {
      setSpeaking(i);
      const u = window.speechSynthesis; const check = setInterval(() => { if (!u.speaking) { clearInterval(check); setSpeaking(null); } }, 300);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: compact ? 8 : 12 }}>
      {items.map((s, i) => {
        const c = CAST[s.who] || { name: s.who, side: 'neutral' };
        const st = SAY_STYLE[s.role || c.side] || SAY_STYLE.neutral;
        const av = compact ? 48 : 58;
        return (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.1 }}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ width: av, height: av, borderRadius: '50%', border: `2.5px solid ${st.bd}`, background: BRAND.navy700, overflow: 'hidden', flexShrink: 0, boxShadow: '0 4px 14px rgba(0,0,0,0.45)' }}>
              <img src={castImg(s.who)} alt={c.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transform: 'scale(1.35)', transformOrigin: 'top center' }} />
            </div>
            <div style={{ position: 'relative', flex: 1, background: st.bg, border: `1.5px solid ${st.bd}66`, borderRadius: 14, padding: compact ? '8px 12px' : '10px 14px', backdropFilter: 'blur(6px)' }}>
              <span style={{ position: 'absolute', left: -8, top: 16, width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderRight: `8px solid ${st.bd}66` }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <p style={{ flex: 1, fontSize: compact ? 'clamp(0.66rem,0.8vw,0.72rem)' : 'clamp(0.66rem,0.85vw,0.74rem)', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: st.name }}>{c.name}{c.role ? ` · ${c.role}` : ''}</p>
                {canSpeak && (
                  <button type="button" onClick={() => onSpeak(i, s)} title={speaking === i ? 'Stop' : `Hear ${c.name}`} aria-label={speaking === i ? 'Stop voice' : `Hear ${c.name} say this`}
                    style={{ width: 24, height: 24, borderRadius: '50%', border: `1.5px solid ${st.bd}80`, background: speaking === i ? st.bd : 'rgba(255,255,255,0.06)', color: speaking === i ? BRAND.navy900 : st.name, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, lineHeight: 1, flexShrink: 0 }}>
                    {speaking === i ? '■' : '🔊'}
                  </button>
                )}
              </div>
              <p style={{ fontSize: compact ? 'clamp(0.86rem,1.05vw,0.96rem)' : 'clamp(0.9rem,1.15vw,1.02rem)', fontWeight: 600, color: st.text, lineHeight: 1.45 }}>{s.text}</p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

/**
 * Dominant colour of an illustration → deep, saturated panel tint that matches the picture
 * but keeps a strong contrast with white text. Returns { h, s } (hue 0-360, saturation 0-1).
 */
const tintCache = {};
function useImageTint(src) {
  const [tint, setTint] = useState(() => (src && tintCache[src]) || null);
  useEffect(() => {
    if (!src) return undefined;
    if (tintCache[src]) { setTint(tintCache[src]); return undefined; }
    let alive = true;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const W = 48, H = 27;
        const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
        const cx = cv.getContext('2d'); cx.drawImage(img, 0, 0, W, H);
        const d = cx.getImageData(0, 0, W, H).data;
        // hue histogram (12 bins) weighted by saturation and by horizontal position:
        // the right side of the picture touches the panel, so it counts more.
        const BINS = 12, hist = new Array(BINS).fill(0), hx = new Array(BINS).fill(0), hy = new Array(BINS).fill(0), hs = new Array(BINS).fill(0);
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i] / 255, g = d[i + 1] / 255, b = d[i + 2] / 255;
          const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, dlt = mx - mn;
          if (dlt < 0.1 || l < 0.08 || l > 0.95) continue; // skip greys, near-black and near-white
          const s = dlt / (1 - Math.abs(2 * l - 1));
          let h = mx === r ? ((g - b) / dlt) % 6 : mx === g ? (b - r) / dlt + 2 : (r - g) / dlt + 4;
          h = (h * 60 + 360) % 360;
          const px = (i / 4) % W;
          const w = s * (0.5 + 1.0 * (px / W));
          const bi = Math.floor(h / (360 / BINS)) % BINS;
          hist[bi] += w; hx[bi] += Math.cos(h * Math.PI / 180) * w; hy[bi] += Math.sin(h * Math.PI / 180) * w; hs[bi] += s * w;
        }
        let best = 0; for (let k = 1; k < BINS; k++) if (hist[k] > hist[best]) best = k;
        // merge the winning bin with its two neighbours for a smoother hue
        const nb = [(best + BINS - 1) % BINS, best, (best + 1) % BINS];
        const sx = nb.reduce((a, k) => a + hx[k], 0), sy = nb.reduce((a, k) => a + hy[k], 0), wsum = nb.reduce((a, k) => a + hist[k], 0), ssum = nb.reduce((a, k) => a + hs[k], 0);
        const res = wsum > 0 ? { h: (Math.atan2(sy, sx) * 180 / Math.PI + 360) % 360, s: Math.min(1, ssum / wsum) } : { h: 245, s: 0.3 };
        tintCache[src] = res;
        if (alive) setTint(res);
      } catch (e) { /* cross-origin or decode issue: keep neutral panel */ }
    };
    img.src = src;
    return () => { alive = false; };
  }, [src]);
  return tint;
}
/** CSS colours for the side panel from a tint (or a neutral fallback). */
function panelColors(tint, override) {
  // override: [h, s] pair in slide data (slide.panelHsl) to force a tint
  const h = override ? override[0] : tint ? Math.round(tint.h) : 222;
  const s = override ? override[1] : tint ? Math.round(Math.min(55, Math.max(34, tint.s * 100 * 0.85))) : 46;
  const at = (l, a = 1) => `hsl(${h} ${s}% ${l}% / ${a})`;
  // The panel starts with the picture's own hue (lightness 14%, so ivory text stays above 12:1
  // contrast) and settles into the C.O.R.E. brand navy on the right.
  const gradient = `linear-gradient(90deg, ${at(14, 0)} 0%, ${at(14, 0.96)} 8%, ${at(14, 1)} 26%, ${BRAND.navy800} 72%, ${BRAND.navy900} 100%)`;
  return { solid: BRAND.navy900, soft: at(20), at, gradient };
}

/** Full-screen picture (chapter covers, thank-you): the image fills the slide, a navy gradient keeps the right side readable. */
// Text panel that shrinks its content (CSS zoom) when it would not fit the available height,
// so short viewports (Chromebooks, projectors at 720p) never need to scroll a slide.
function FitPanel({ style, innerStyle, children }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  useLayoutEffect(() => {
    const outer = outerRef.current, inner = innerRef.current;
    if (!outer || !inner || typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const fit = () => {
      raf = 0;
      inner.style.zoom = '1';
      const cs = getComputedStyle(outer);
      const avail = outer.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      const need = inner.scrollHeight;
      const z = avail > 0 && need > avail + 1 ? Math.max(0.66, avail / need) : 1;
      inner.style.zoom = z.toFixed(3);
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(fit); };
    const ro = new ResizeObserver(schedule);
    ro.observe(outer);
    ro.observe(inner);
    schedule();
    const late = setTimeout(schedule, 400); // fonts / late layout
    return () => { ro.disconnect(); if (raf) cancelAnimationFrame(raf); clearTimeout(late); };
  }, []);
  return (
    <div ref={outerRef} style={style}>
      <div ref={innerRef} style={innerStyle}>{children}</div>
    </div>
  );
}

/** True on phone-sized viewports: scene slides then stack the picture on top and the text at the bottom. */
function useNarrow(query = '(max-width: 640px)') {
  const get = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = e => setNarrow(e.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, [query]);
  return narrow;
}

const ROTATE_KEY = 'core-lesson-rotate-dismissed';
/** Phones held upright: invite the student to turn the phone sideways (lessons are laid out for landscape). Dismissible for the session. */
function RotatePrompt() {
  const touch = useNarrow('(pointer: coarse)');
  const portrait = useNarrow('(orientation: portrait)');
  const small = useNarrow('(max-width: 768px)');
  const [dismissed, setDismissed] = useState(() => { try { return sessionStorage.getItem(ROTATE_KEY) === '1'; } catch (e) { return false; } });
  const show = touch && portrait && small && !dismissed;
  const dismiss = () => { setDismissed(true); try { sessionStorage.setItem(ROTATE_KEY, '1'); } catch (e) { /* private mode */ } };
  return (
    <AnimatePresence>
      {show && (
        <motion.div key="rotate" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, padding: 28, textAlign: 'center', background: 'rgba(7,20,45,0.96)', backdropFilter: 'blur(10px)', color: BRAND.ivory, fontFamily: BRAND.font }}>
          <style>{`@keyframes coreRotatePhone{0%,15%{transform:rotate(0deg)}45%,70%{transform:rotate(-90deg)}100%{transform:rotate(0deg)}}`}</style>
          <div style={{ width: 54, height: 92, borderRadius: 12, border: `3px solid ${BRAND.goldLight}`, position: 'relative', animation: 'coreRotatePhone 2.4s ease-in-out infinite', boxShadow: '0 0 0 8px rgba(226,184,120,0.08)' }}>
            <span style={{ position: 'absolute', left: '50%', bottom: 7, width: 14, height: 3, borderRadius: 2, background: BRAND.goldLight, transform: 'translateX(-50%)' }} />
          </div>
          <div style={{ fontWeight: 900, fontSize: '1.35rem', lineHeight: 1.15 }}>Rotate your phone</div>
          <p style={{ margin: 0, maxWidth: 280, fontWeight: 600, fontSize: '0.92rem', lineHeight: 1.5, color: 'rgba(249,240,231,0.75)' }}>Lessons look best sideways: pictures, missions and Tim's notes all fit on screen. Swipe left or right to change slide.</p>
          <button type="button" onClick={dismiss}
            style={{ marginTop: 6, padding: '10px 20px', borderRadius: 999, background: 'transparent', border: '1.5px solid rgba(226,184,120,0.45)', color: BRAND.goldLight, fontWeight: 800, fontSize: '0.84rem', cursor: 'pointer', fontFamily: 'inherit' }}>
            Continue in portrait
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function FullBleed({ src, imgPos, pos, panelW = 48, strong = false, kenBurns = false }) {
  const n = '7,20,45';
  // the horizontal shade is anchored to the left edge of the text panel (s = 100 - panelW)
  const s = 100 - panelW;
  const grad = pos === 'bottom'
    ? `linear-gradient(180deg, rgba(${n},0.3) 0%, rgba(${n},0) 18%, rgba(${n},0) 48%, rgba(${n},0.78) 70%, rgba(${n},0.95) 100%)`
    : strong
      ? `linear-gradient(90deg, rgba(${n},0) 0%, rgba(${n},0) ${s - 24}%, rgba(${n},0.66) ${s - 6}%, rgba(${n},0.93) ${s + 6}%, rgba(${n},0.96) 100%), linear-gradient(180deg, rgba(${n},0.3) 0%, rgba(${n},0) 20%, rgba(${n},0) 72%, rgba(${n},0.5) 100%)`
      : `linear-gradient(90deg, rgba(${n},0) 0%, rgba(${n},0) ${s - 18}%, rgba(${n},0.55) ${s - 2}%, rgba(${n},0.88) ${s + 12}%, rgba(${n},0.94) 100%), linear-gradient(180deg, rgba(${n},0.35) 0%, rgba(${n},0) 22%, rgba(${n},0) 70%, rgba(${n},0.55) 100%)`;
  const anim = kenBurns
    ? { initial: { opacity: 0, scale: 1.0 }, animate: { opacity: 1, scale: 1.08 }, transition: { opacity: { duration: 0.9 }, scale: { duration: 18, ease: 'linear' } } }
    : { initial: { opacity: 0, scale: 1.06 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.9 } };
  return (
    <>
      <motion.img src={src} alt="" {...anim}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: imgPos || 'center', zIndex: 0, transformOrigin: kenBurns ? '30% 50%' : 'center' }} />
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', background: grad }} />
    </>
  );
}

/** True for a chapter title slide: scene with badge + title and no content blocks. */
function isCoverSlide(s) {
  return !!s && s.layout === 'scene' && !!(s.badge && s.title) && !(s.grid || s.cards || s.quiz || s.bullets || s.scenario || s.objectiveItems || s.recapItems || s.rules || s.encrypt || s.say || s.sorter || s.passwordLab || s.phone || s.branch);
}

const NUM_WORDS = ['01', '02', '03', '04', '05', '06', '07', '08', '09'];

/**
 * Chapter cover variants (slide.coverStyle):
 *  'cinematic' — full picture with slow zoom, giant chapter numeral, title bottom-left, map of the stops
 *  'briefing'  — picture + right panel with the chapter guide and the badge you can earn
 *  'mission'   — cinematic + the briefing card
 *  'letterbox' — cinema bars, title revealed letter by letter
 */
function ChapterCover({ slide, meta, t }) {
  const style = slide.coverStyle;
  const n = NUM_WORDS[meta.n - 1] || String(meta.n);
  const host = slide.host && CAST[slide.host] ? { key: slide.host, ...CAST[slide.host] } : null;
  const gold = BRAND.goldLight;
  const ivory = BRAND.ivory;
  const titleFs = 'clamp(2rem, 4.6vw, 3.9rem)';
  const chapterWord = (slide.badge || '').replace(/^capture\s*/i, '');

  const Stops = ({ center, mt }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px,0.8vw,10px)', marginTop: mt !== undefined ? mt : 'clamp(14px,1.8vw,22px)', flexWrap: 'wrap', justifyContent: center ? 'center' : 'flex-start' }}>
      {meta.stops.map((s, i) => {
        const k = i + 1; const done = k < meta.n; const now = k === meta.n;
        return (
          <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.07 }}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: now ? 26 : 18, height: now ? 26 : 18, borderRadius: '50%', background: now ? `linear-gradient(135deg, ${gold}, ${BRAND.gold})` : done ? 'rgba(226,184,120,0.25)' : 'rgba(249,240,231,0.08)', border: `1.5px solid ${now || done ? gold : 'rgba(249,240,231,0.3)'}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: now ? '0.72rem' : '0.6rem', color: now ? BRAND.navy900 : done ? gold : 'rgba(249,240,231,0.55)', boxShadow: now ? '0 0 18px rgba(226,184,120,0.55)' : 'none', flexShrink: 0 }}>{done ? '✓' : k}</span>
            <span style={{ fontWeight: now ? 800 : 600, fontSize: 'clamp(0.66rem,0.82vw,0.78rem)', color: now ? ivory : 'rgba(249,240,231,0.55)', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>{s}</span>
            {i < meta.stops.length - 1 && <span style={{ width: 'clamp(10px,1.4vw,22px)', height: 1, background: 'rgba(226,184,120,0.35)', marginLeft: 2 }} />}
          </motion.div>
        );
      })}
    </div>
  );

  const Briefing = ({ compact }) => (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.45 }}
      style={{ marginTop: compact ? 'clamp(12px,1.6vw,20px)' : 'clamp(16px,2.2vw,28px)', display: 'grid', gridTemplateColumns: host && meta.award ? '1fr 1fr' : '1fr', gap: 'clamp(8px,1vw,14px)', width: '100%', maxWidth: 640 }}>
      {host && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'clamp(8px,1vw,12px) clamp(10px,1.2vw,14px)', borderRadius: 14, background: 'rgba(7,20,45,0.55)', border: '1.5px solid rgba(226,184,120,0.35)', backdropFilter: 'blur(10px)' }}>
          <span style={{ width: 'clamp(44px,4.6vw,60px)', height: 'clamp(44px,4.6vw,60px)', borderRadius: '50%', overflow: 'hidden', border: `2px solid ${gold}`, background: BRAND.navy700, flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
            <img src={castImg(host.key)} alt={host.name} style={{ width: '150%', height: 'auto', marginTop: '4%' }} />
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: 'clamp(0.6rem,0.74vw,0.7rem)', letterSpacing: '0.16em', textTransform: 'uppercase', color: gold }}>Your guide</span>
            <span style={{ fontWeight: 900, fontSize: 'clamp(0.95rem,1.25vw,1.15rem)', color: ivory, lineHeight: 1.15 }}>{host.name}</span>
            <span style={{ fontWeight: 600, fontSize: 'clamp(0.7rem,0.88vw,0.82rem)', color: 'rgba(249,240,231,0.7)' }}>{host.role}</span>
          </span>
        </div>
      )}
      {meta.award && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'clamp(8px,1vw,12px) clamp(10px,1.2vw,14px)', borderRadius: 14, background: 'rgba(7,20,45,0.55)', border: '1.5px dashed rgba(226,184,120,0.5)', backdropFilter: 'blur(10px)' }}>
          <span style={{ width: 'clamp(44px,4.6vw,60px)', height: 'clamp(44px,4.6vw,60px)', borderRadius: '50%', border: '2px dashed rgba(226,184,120,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'clamp(1.3rem,2vw,1.8rem)', filter: 'grayscale(1) opacity(0.75)', flexShrink: 0 }}>{meta.award.icon}</span>
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: 'clamp(0.6rem,0.74vw,0.7rem)', letterSpacing: '0.16em', textTransform: 'uppercase', color: gold }}>Badge to earn</span>
            <span style={{ fontWeight: 900, fontSize: 'clamp(0.95rem,1.25vw,1.15rem)', color: ivory, lineHeight: 1.15 }}>{meta.award.name}</span>
            <span style={{ fontWeight: 600, fontSize: 'clamp(0.7rem,0.88vw,0.82rem)', color: 'rgba(249,240,231,0.7)' }}>
              {[meta.missions ? `${meta.missions} mission${meta.missions > 1 ? 's' : ''}` : null, meta.quizzes ? `${meta.quizzes} quiz` : null].filter(Boolean).join(' · ') || 'Reach the end of the chapter'}
            </span>
          </span>
        </div>
      )}
    </motion.div>
  );

  const Badge = () => (
    <motion.span initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      style={{ display: 'inline-block', alignSelf: 'flex-start', padding: '6px 16px', borderRadius: 20, background: `linear-gradient(135deg, ${gold}, ${BRAND.gold})`, fontWeight: 900, fontSize: 'clamp(0.66rem,0.85vw,0.8rem)', color: BRAND.navy900, textTransform: 'uppercase', letterSpacing: '0.12em', boxShadow: '0 4px 14px rgba(205,155,80,0.3)', marginBottom: 'clamp(10px,1.4vw,16px)' }}>
      {slide.badge}
    </motion.span>
  );
  const Title = ({ fs, center }) => (
    <motion.h2 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
      style={{ fontSize: fs || titleFs, fontWeight: 900, lineHeight: 1.05, margin: 0, textAlign: center ? 'center' : 'left', background: `linear-gradient(135deg, ${ivory} 0%, ${gold} 60%, ${BRAND.gold} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.45))' }}>
      {slide.title}
    </motion.h2>
  );
  const Line = ({ center }) => (
    <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.35, duration: 0.6, ease: 'easeOut' }}
      style={{ width: 'clamp(70px,8vw,120px)', height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${gold}, ${BRAND.gold})`, margin: center ? 'clamp(10px,1.4vw,16px) auto' : 'clamp(10px,1.4vw,16px) 0', transformOrigin: center ? 'center' : 'left' }} />
  );
  const Sub = ({ center }) => slide.sub ? (
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
      style={{ fontSize: 'clamp(0.95rem,1.35vw,1.25rem)', fontWeight: 600, color: 'rgba(249,240,231,0.92)', lineHeight: 1.55, margin: 0, maxWidth: 680, textAlign: center ? 'center' : 'left', textShadow: '0 1px 8px rgba(0,0,0,0.45)' }}>
      {slide.sub}
    </motion.p>
  ) : null;

  const Numeral = ({ right }) => (
    <motion.span aria-hidden initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1.1, ease: 'easeOut' }}
      style={{ position: 'absolute', top: '-4%', right: right || 'clamp(16px,3vw,56px)', zIndex: 2, fontSize: 'clamp(13rem, 30vw, 26rem)', fontWeight: 900, lineHeight: 1, letterSpacing: '-0.05em', color: 'rgba(226,184,120,0.07)', WebkitTextStroke: `3px rgba(226,184,120,0.55)`, filter: 'drop-shadow(0 0 30px rgba(226,184,120,0.25))', pointerEvents: 'none', userSelect: 'none', fontFamily: BRAND.font }}>
      {n}
    </motion.span>
  );

  // ── A / A+B: cinematic ────────────────────────────────────────────────────
  if (style === 'cinematic' || style === 'mission') {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: BRAND.navy900, position: 'relative' }}>
        <FullBleed src={slide.img} imgPos={slide.imgPos} pos="bottom" kenBurns />
        <Numeral />
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 3, padding: '0 clamp(28px,4vw,64px) clamp(24px,3vw,44px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ fontWeight: 800, letterSpacing: '0.3em', fontSize: 'clamp(0.62rem,0.8vw,0.76rem)', color: gold, textTransform: 'uppercase', marginBottom: 8 }}>Capture {meta.n} of {meta.total}{host ? ` · with ${host.name}` : ''}</span>
          <Title />
          <Line />
          <Sub />
          {style === 'mission' ? <Briefing compact /> : <Stops />}
        </div>
      </div>
    );
  }

  // ── B: briefing panel on the right ───────────────────────────────────────
  if (style === 'briefing') {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: BRAND.navy900, position: 'relative' }}>
        <FullBleed src={slide.img} imgPos={slide.imgPos} panelW={50} />
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '50%', zIndex: 3, padding: 'clamp(16px,2.4vw,32px) clamp(22px,2.8vw,44px) clamp(22px,2.8vw,38px) clamp(20px,3vw,48px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Badge />
          <Title fs="clamp(1.9rem,4vw,3.3rem)" />
          <Line />
          <Sub />
          <Briefing />
          <Stops />
        </div>
      </div>
    );
  }

  // ── C: letterbox ─────────────────────────────────────────────────────────
  const letters = Array.from(slide.title || '');
  return (
    <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: '#000', position: 'relative' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: '10%', bottom: '10%', overflow: 'hidden' }}>
        <FullBleed src={slide.img} imgPos={slide.imgPos} pos="bottom" kenBurns />
      </div>
      <motion.div initial={{ scaleY: 1.4 }} animate={{ scaleY: 1 }} transition={{ duration: 0.8, ease: 'easeOut' }} style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '10%', background: '#000', zIndex: 2, transformOrigin: 'top' }} />
      <motion.div initial={{ scaleY: 1.4 }} animate={{ scaleY: 1 }} transition={{ duration: 0.8, ease: 'easeOut' }} style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '10%', background: '#000', zIndex: 2, transformOrigin: 'bottom' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '10%', zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 clamp(20px,3vw,48px)', boxSizing: 'border-box' }}>
        <span style={{ fontWeight: 800, letterSpacing: '0.32em', fontSize: 'clamp(0.6rem,0.78vw,0.74rem)', color: gold, textTransform: 'uppercase' }}>C.O.R.E. for Schools</span>
        <span style={{ fontWeight: 800, letterSpacing: '0.32em', fontSize: 'clamp(0.6rem,0.78vw,0.74rem)', color: 'rgba(249,240,231,0.6)', textTransform: 'uppercase' }}>Capture {n} / {NUM_WORDS[meta.total - 1]}</span>
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: '10%', zIndex: 3, padding: '0 clamp(28px,4vw,64px) clamp(20px,2.6vw,36px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <motion.span initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '5px 14px', border: `1.5px solid ${gold}`, color: gold, fontWeight: 800, letterSpacing: '0.3em', fontSize: 'clamp(0.62rem,0.8vw,0.76rem)', textTransform: 'uppercase', marginBottom: 'clamp(12px,1.6vw,20px)' }}>
          <span style={{ width: 6, height: 6, background: gold, transform: 'rotate(45deg)' }} /> Capture {chapterWord} <span style={{ width: 6, height: 6, background: gold, transform: 'rotate(45deg)' }} />
        </motion.span>
        <h2 aria-label={slide.title} style={{ margin: 0, fontSize: 'clamp(1.9rem,4.4vw,3.6rem)', fontWeight: 900, lineHeight: 1.08, letterSpacing: '0.14em', textTransform: 'uppercase', color: ivory, textShadow: '0 2px 18px rgba(0,0,0,0.6)' }}>
          {letters.map((ch, i) => (
            <motion.span key={i} aria-hidden initial={{ opacity: 0, y: 10, filter: 'blur(6px)' }} animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }} transition={{ delay: 0.35 + i * 0.035, duration: 0.35 }} style={{ display: 'inline-block', whiteSpace: 'pre' }}>{ch}</motion.span>
          ))}
        </h2>
        <Line center />
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 + letters.length * 0.035 + 0.2 }}
          style={{ fontSize: 'clamp(0.95rem,1.3vw,1.2rem)', fontWeight: 600, fontStyle: 'italic', color: 'rgba(249,240,231,0.9)', lineHeight: 1.5, margin: 0, maxWidth: 720, textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>
          {slide.sub}
        </motion.p>
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '10%', zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stops center mt={0} />
      </div>
    </div>
  );
}

/** Full-figure cutouts anchored bottom-right: slide.cast = ['trace', { who: 'tim', h: 48 }] (h = % of slide height) */
function CastFigures({ cast }) {
  if (!cast || !cast.length) return null;
  const items = cast.map(c => (typeof c === 'string' ? { who: c } : c));
  // lay out right-to-left, slight overlap, tallest first visually
  let right = 2;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 3 }}>
      {items.map((c, i) => {
        const h = c.h || 52;
        const r = right; right += (c.w || 14) * 0.78;
        const meta = CAST[c.who] || { name: c.who };
        return (
          <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08, duration: 0.45 }}
            style={{ position: 'absolute', bottom: 0, right: `${r}%`, height: `${h}%`, zIndex: 10 - i, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {c.label !== false && (
              <span style={{ marginBottom: 4, padding: '3px 10px', borderRadius: 999, background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', fontWeight: 800, fontSize: 'clamp(0.62rem,0.8vw,0.74rem)', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{meta.name}</span>
            )}
            <img src={castImg(c.who)} alt={meta.name} style={{ height: '100%', width: 'auto', filter: 'drop-shadow(0 10px 22px rgba(0,0,0,0.6))' }} />
          </motion.div>
        );
      })}
    </div>
  );
}

function Quiz({ d, t, compact, fluid, onDone }) {
  const [sel, setSel] = useState(null);
  const [done, setDone] = useState(false);
  const correct = sel === d.correctIndex;
  const doneRef = useRef(false);
  useEffect(() => { if (done && !doneRef.current) { doneRef.current = true; onDone && onDone(); } }, [done, onDone]);
  const pad = compact ? 'clamp(12px,1.8vw,22px)' : 'clamp(18px,2.5vw,32px)';
  const qFont = compact ? 'clamp(0.92rem,1.3vw,1.08rem)' : 'clamp(1rem,1.6vw,1.25rem)';
  const oFont = compact ? 'clamp(0.86rem,1.2vw,1rem)' : 'clamp(0.95rem,1.4vw,1.12rem)';
  const oPad = compact ? 'clamp(8px,1.2vw,13px)' : 'clamp(12px,1.6vw,18px)';
  const iSz = compact ? 26 : 30;
  return (
    <div style={{ background: t.card, border: `2px solid ${t.cardBd}`, borderRadius: compact ? 14 : 18, padding: pad, backdropFilter: 'blur(8px)', width: '100%', maxWidth: fluid ? '100%' : compact ? 'calc(100% - 75px)' : 720, boxSizing: 'border-box', margin: '0 auto', alignSelf: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 10 : 12, marginBottom: compact ? 10 : 14 }}>
        <div style={{ width: compact ? 30 : 36, height: compact ? 30 : 36, borderRadius: compact ? 8 : 10, background: `${t.acc}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: compact ? '0.95rem' : '1.1rem', fontWeight: 900, color: t.acc }}>?</span>
        </div>
        <p style={{ fontWeight: 800, fontSize: qFont, color: '#fff', lineHeight: 1.3, flex: 1 }}>{d.question}</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 6 : 8 }}>
        {d.options.map((o, i) => {
          let bg = 'rgba(255,255,255,0.18)', bd = '2px solid rgba(255,255,255,0.3)', col = '#fff';
          if (sel === i && !done) { bg = `${t.acc}30`; bd = `2px solid ${t.acc}`; col = '#fff'; }
          if (done && i === d.correctIndex) { bg = 'rgba(16,185,129,0.3)'; bd = '2px solid #10b981'; col = '#fff'; }
          if (done && sel === i && !correct) { bg = 'rgba(239,68,68,0.25)'; bd = '2px solid #ef4444'; }
          return (
            <motion.button key={i} type="button" whileHover={!done ? { scale: 1.01 } : {}} whileTap={!done ? { scale: 0.99 } : {}}
              onClick={() => !done && setSel(i)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: compact ? 8 : 10, padding: oPad, borderRadius: compact ? 10 : 12, background: bg, border: bd, cursor: done ? 'default' : 'pointer', color: col, fontWeight: 800, fontSize: oFont, textAlign: 'left', transition: 'all 0.2s', backdropFilter: 'blur(6px)' }}>
              <span style={{ width: iSz, height: iSz, borderRadius: 8, background: sel === i ? `${t.acc}40` : 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: compact ? '0.78rem' : '0.85rem', flexShrink: 0, border: `2px solid ${sel === i ? t.acc : 'rgba(255,255,255,0.35)'}`, color: '#fff' }}>{String.fromCharCode(65 + i)}</span>
              <span style={{ flex: 1, lineHeight: 1.4 }}>{o}</span>
              {done && i === d.correctIndex && <FiCheck size={compact ? 16 : 18} color="#34d399" strokeWidth={3} />}
              {done && sel === i && !correct && <FiX size={compact ? 16 : 18} color="#f87171" strokeWidth={3} />}
            </motion.button>
          );
        })}
      </div>
      {!done && (
        <motion.button type="button" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
          onClick={() => sel !== null && setDone(true)}
          style={{ marginTop: compact ? 10 : 14, padding: compact ? '10px 26px' : '12px 32px', borderRadius: 24, background: sel !== null ? `linear-gradient(135deg, ${t.acc}, ${t.acc2})` : 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 900, fontSize: compact ? 'clamp(0.85rem,1.1vw,0.95rem)' : 'clamp(0.95rem,1.3vw,1.08rem)', border: 'none', cursor: sel !== null ? 'pointer' : 'default', opacity: sel !== null ? 1 : 0.3, boxShadow: sel !== null ? `0 4px 20px ${t.acc}40` : 'none' }}>
          Check Answer
        </motion.button>
      )}
      {done && d.explanation && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          style={{ marginTop: compact ? 8 : 12, padding: compact ? 'clamp(10px,1.5vw,16px)' : 'clamp(14px,2vw,22px)', borderRadius: compact ? 10 : 12, background: correct ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.08)', border: `1.5px solid ${correct ? '#10b98140' : '#ef444440'}` }}>
          <p style={{ fontWeight: 900, color: correct ? '#34d399' : '#fca5a5', fontSize: compact ? 'clamp(0.88rem,1.2vw,1rem)' : 'clamp(1rem,1.4vw,1.15rem)', marginBottom: compact ? 4 : 6 }}>{correct ? 'Correct!' : 'Not quite!'}</p>
          <p style={{ fontWeight: 600, color: 'rgba(255,255,255,0.7)', fontSize: compact ? 'clamp(0.8rem,1vw,0.9rem)' : 'clamp(0.9rem,1.2vw,1.02rem)', lineHeight: 1.5 }}>{d.explanation}</p>
        </motion.div>
      )}
    </div>
  );
}

function Cards({ items, t }) {
  const [open, setOpen] = useState(null);
  const n = items.length;
  const cols = n <= 2 ? n : (n === 4 || n === 8) ? 2 : n <= 6 ? 3 : 4;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, alignItems: 'stretch' }}>
      {items.map((c, i) => {
        const isOpen = open === i;
        return (
          <motion.div key={i} whileHover={{ y: -2 }} onClick={() => setOpen(isOpen ? null : i)}
            style={{ padding: 'clamp(14px,2vw,20px)', borderRadius: 16, background: isOpen ? `${t.acc}18` : t.card, border: `2px solid ${isOpen ? t.acc + '50' : t.cardBd}`, cursor: 'pointer', transition: 'background 0.25s, border 0.25s', backdropFilter: 'blur(12px)', boxShadow: '0 3px 16px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ flex: 1, fontWeight: 800, fontSize: 'clamp(0.9rem,1.2vw,1.02rem)', color: '#fff', lineHeight: 1.3 }}>{c.title}</span>
              <motion.span animate={{ rotate: isOpen ? 180 : 0 }} style={{ color: t.acc }}><FiChevronDown size={18} /></motion.span>
            </div>
            <AnimatePresence>
              {isOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1, marginTop: 12 }} exit={{ height: 0, opacity: 0, marginTop: 0 }} style={{ overflow: 'hidden' }}>
                  <p style={{ fontWeight: 600, fontSize: 'clamp(0.85rem,1.1vw,0.95rem)', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>{c.desc || c.text}</p>
                  {c.examples && <p style={{ marginTop: 8, fontWeight: 700, fontSize: 'clamp(0.78rem,1vw,0.88rem)', color: t.acc, opacity: 0.7 }}>Examples: {c.examples}</p>}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}

function CIA({ t }) {
  const [active, setActive] = useState(null);
  const pillars = [
    { k: 'C', l: 'Confidentiality', c: '#818cf8', desc: 'Your personal information (photos, passwords, messages) is confidential and must be protected. Only authorized people should have access.' },
    { k: 'I', l: 'Integrity', c: '#2dd4bf', desc: 'Information should always be correct and not be changed by people who aren\'t allowed to do so.' },
    { k: 'A', l: 'Availability', c: '#4ade80', desc: 'Services, such as online games and websites, must be operational and easily accessible whenever you need them.' },
  ];
  return (
    <div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 16 }}>
        {pillars.map(p => {
          const isA = active === p.k;
          return (
            <motion.button key={p.k} type="button" whileHover={{ scale: 1.04, y: -3 }} whileTap={{ scale: 0.97 }}
              onClick={() => setActive(isA ? null : p.k)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: 'clamp(120px,14vw,160px)', padding: 'clamp(16px,2.5vw,24px) 12px', borderRadius: 18, background: isA ? `${p.c}18` : t.card, border: `2.5px solid ${isA ? p.c : t.cardBd}`, cursor: 'pointer', transition: 'all 0.25s', boxShadow: isA ? `0 4px 24px ${p.c}25` : 'none' }}>
              <span style={{ width: 'clamp(40px,5vw,56px)', height: 'clamp(40px,5vw,56px)', borderRadius: '50%', background: `${p.c}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'clamp(1.2rem,2vw,1.6rem)', fontWeight: 900, color: p.c }}>{p.k}</span>
              <span style={{ fontWeight: 900, fontSize: 'clamp(0.78rem,1.1vw,0.95rem)', color: isA ? p.c : '#fff', letterSpacing: '0.02em' }}>{p.l}</span>
            </motion.button>
          );
        })}
      </div>
      <AnimatePresence mode="wait">
        {active && (() => {
          const p = pillars.find(x => x.k === active);
          return (
            <motion.div key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              style={{ padding: 'clamp(16px,2.5vw,24px)', borderRadius: 16, background: `${p.c}0a`, border: `1.5px solid ${p.c}30`, textAlign: 'center' }}>
              <p style={{ fontWeight: 700, fontSize: 'clamp(0.92rem,1.3vw,1.08rem)', color: 'rgba(255,255,255,0.88)', lineHeight: 1.6 }}>{p.desc}</p>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}

const DEFAULT_RULES = [
  { title: 'Use strong passwords', sub: 'Mix upper/lowercase, numbers, symbols. At least 12 characters!' },
  { title: 'Enable two-factor authentication', sub: 'Add an extra layer with a code sent to your phone.' },
  { title: 'Be cautious of links & attachments', sub: 'Don\'t click suspicious links. Verify the sender.' },
  { title: 'Keep software up-to-date', sub: 'Updates patch security vulnerabilities. Enable auto-updates!' },
  { title: 'Back up your data', sub: 'Use cloud storage or an external drive regularly.' },
];

/** slide.rules = true (default list) or an array of { title, sub } */
function Rules({ t, items }) {
  const rules = (Array.isArray(items) ? items : DEFAULT_RULES).map((r, i) => ({ ...r, icon: String(i + 1) }));
  const [checked, setChecked] = useState([]);
  const toggle = i => setChecked(p => p.includes(i) ? p.filter(v => v !== i) : [...p, i]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {rules.map((r, i) => {
        const done = checked.includes(i);
        return (
          <motion.button key={i} type="button" whileHover={{ scale: 1.01, x: 2 }} whileTap={{ scale: 0.99 }}
            onClick={() => toggle(i)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 'clamp(7px,1.2vw,11px)', borderRadius: 10, background: done ? 'rgba(16,185,129,0.12)' : t.card, border: `1.5px solid ${done ? '#10b98140' : t.cardBd}`, cursor: 'pointer', textAlign: 'left', transition: 'all 0.25s' }}>
            <span style={{ width: 26, height: 26, borderRadius: 7, background: done ? 'rgba(16,185,129,0.15)' : `${t.acc}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem', color: done ? '#10b981' : t.acc, flexShrink: 0, border: `1.5px solid ${done ? '#10b98130' : t.acc + '25'}` }}>{r.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontWeight: 800, fontSize: 'clamp(0.78rem,1vw,0.88rem)', color: '#fff', textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.45 : 1, lineHeight: 1.25 }}>{r.title}</p>
              <p style={{ fontWeight: 600, fontSize: 'clamp(0.68rem,0.85vw,0.76rem)', color: 'rgba(255,255,255,0.7)', marginTop: 2, lineHeight: 1.3 }}>{r.sub}</p>
            </div>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: done ? '#10b981' : 'rgba(255,255,255,0.06)', border: done ? 'none' : `1.5px solid ${t.cardBd}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
              {done && <FiCheck size={12} color="#fff" strokeWidth={3} />}
            </div>
          </motion.button>
        );
      })}
      <AnimatePresence>
        {checked.length === 5 && (
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
            style={{ padding: '16px', borderRadius: 14, background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(45,212,191,0.1))', border: '2px solid #10b98140', textAlign: 'center' }}>
            <p style={{ fontWeight: 900, color: '#34d399', fontSize: 'clamp(1rem,1.5vw,1.2rem)' }}>Cyber Hero! All rules mastered!</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Encrypt({ t }) {
  const [val, setVal] = useState('');
  const [show, setShow] = useState(false);
  const enc = useMemo(() => val.split('').map(c => /[a-z]/i.test(c) ? String.fromCharCode(((c.charCodeAt(0) - (c === c.toUpperCase() ? 65 : 97) + 3) % 26) + (c === c.toUpperCase() ? 65 : 97)) : c).join(''), [val]);
  return (
    <div style={{ background: t.card, border: `2px solid ${t.cardBd}`, borderRadius: 18, padding: 'clamp(18px,2.5vw,28px)', backdropFilter: 'blur(8px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span style={{ width: 'clamp(36px,4vw,48px)', height: 'clamp(36px,4vw,48px)', borderRadius: 12, background: `${t.acc}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'clamp(1.2rem,2vw,1.6rem)', fontWeight: 900, color: t.acc, border: `1.5px solid ${t.acc}25` }}>E</span>
        <div>
          <p style={{ fontWeight: 900, fontSize: 'clamp(1rem,1.4vw,1.15rem)', color: t.acc }}>Encryption Lab</p>
          <p style={{ fontWeight: 600, fontSize: 'clamp(0.78rem,1vw,0.88rem)', color: 'rgba(255,255,255,0.75)' }}>Type a secret message and see it encrypted!</p>
        </div>
      </div>
      <input value={val} onChange={e => { setVal(e.target.value); setShow(false); }} placeholder="Your secret message..."
        style={{ width: '100%', padding: 'clamp(12px,1.5vw,16px)', borderRadius: 14, background: 'rgba(255,255,255,0.06)', border: `2px solid ${t.cardBd}`, color: '#fff', fontSize: 'clamp(0.95rem,1.3vw,1.1rem)', fontWeight: 700, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
        onFocus={e => { e.target.style.borderColor = t.acc; }} onBlur={e => { e.target.style.borderColor = t.cardBd; }} />
      {val && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: 16 }}>
          <p style={{ fontSize: 'clamp(0.72rem,0.9vw,0.82rem)', fontWeight: 700, color: 'rgba(255,255,255,0.65)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Encrypted (Caesar Cipher +3):</p>
          <div style={{ padding: 'clamp(12px,1.5vw,16px)', borderRadius: 12, background: 'rgba(251,191,36,0.08)', border: '2px solid rgba(251,191,36,0.2)', fontFamily: 'monospace', fontWeight: 900, color: '#fbbf24', letterSpacing: 3, wordBreak: 'break-all', fontSize: 'clamp(1rem,1.4vw,1.2rem)' }}>{enc}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <motion.button type="button" whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={() => setShow(!show)}
              style={{ padding: '8px 20px', borderRadius: 20, background: show ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)', border: `1.5px solid ${show ? '#10b98140' : 'rgba(255,255,255,0.1)'}`, color: '#fff', fontWeight: 700, fontSize: 'clamp(0.82rem,1vw,0.92rem)', cursor: 'pointer' }}>
              {show ? 'Hide Original' : 'Decrypt'}
            </motion.button>
          </div>
          <AnimatePresence>
            {show && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto', marginTop: 10 }} exit={{ opacity: 0, height: 0 }}
                style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(16,185,129,0.08)', border: '1.5px solid rgba(16,185,129,0.2)', overflow: 'hidden' }}>
                <p style={{ fontFamily: 'monospace', fontWeight: 900, color: '#34d399', fontSize: 'clamp(1rem,1.4vw,1.2rem)', letterSpacing: 2 }}>{val}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}

function Scenario({ data, t }) {
  const [step, setStep] = useState(0);
  const [sel, setSel] = useState(null);
  const [results, setResults] = useState([]);
  const [finished, setFinished] = useState(false);
  const current = data[step];
  const isLast = step === data.length - 1;

  const handlePick = (i) => {
    if (sel !== null) return;
    setSel(i);
    setResults(prev => [...prev, i === current.correctIndex]);
  };

  const handleNext = () => {
    if (isLast) {
      setFinished(true);
    } else {
      setSel(null);
      setStep(s => s + 1);
    }
  };

  if (finished) {
    const score = results.filter(Boolean).length;
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        style={{ background: t.card, border: `2px solid ${t.cardBd}`, borderRadius: 18, padding: 'clamp(20px,3vw,32px)', backdropFilter: 'blur(8px)', textAlign: 'center' }}>
        <p style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 900, color: score === data.length ? '#10b981' : t.acc, marginBottom: 8 }}>
          {score === data.length ? 'Perfect!' : score >= data.length / 2 ? 'Good job!' : 'Keep practicing!'}
        </p>
        <p style={{ fontSize: 'clamp(1rem,1.5vw,1.2rem)', fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>
          You got {score} out of {data.length} scenarios right.
        </p>
        <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={() => { setStep(0); setSel(null); setResults([]); setFinished(false); }}
          style={{ marginTop: 16, padding: '10px 28px', borderRadius: 22, background: `linear-gradient(135deg, ${t.acc}, ${t.acc2 || t.acc})`, border: 'none', color: '#fff', fontWeight: 800, fontSize: 'clamp(0.9rem,1.2vw,1rem)', cursor: 'pointer' }}>
          Try Again
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
      style={{ background: t.card, border: `2px solid ${t.cardBd}`, borderRadius: 18, padding: 'clamp(16px,2.5vw,28px)', backdropFilter: 'blur(8px)' }}>
      {data.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ padding: '4px 14px', borderRadius: 20, background: `${t.acc}18`, border: `1.5px solid ${t.acc}30`, fontWeight: 800, fontSize: 'clamp(0.75rem,1vw,0.85rem)', color: t.acc }}>
            Scenario {step + 1} of {data.length}
          </span>
        </div>
      )}
      <p style={{ fontSize: 'clamp(0.95rem,1.3vw,1.1rem)', fontWeight: 800, color: '#fff', lineHeight: 1.5, marginBottom: 4 }}>{current.situation}</p>
      {current.context && <p style={{ fontSize: 'clamp(0.82rem,1.1vw,0.92rem)', fontWeight: 600, color: 'rgba(255,255,255,0.65)', lineHeight: 1.45, marginBottom: 14 }}>{current.context}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        {current.options.map((o, i) => {
          const picked = sel === i;
          const isCorrect = i === current.correctIndex;
          const showResult = sel !== null;
          return (
            <motion.button key={i} whileHover={sel === null ? { scale: 1.01, x: 4 } : {}} whileTap={sel === null ? { scale: 0.99 } : {}} onClick={() => handlePick(i)}
              style={{ padding: 'clamp(10px,1.5vw,14px) clamp(14px,2vw,20px)', borderRadius: 14, background: showResult ? (isCorrect ? 'rgba(16,185,129,0.15)' : picked ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.04)') : 'rgba(255,255,255,0.06)',
                border: `2px solid ${showResult ? (isCorrect ? '#10b98150' : picked ? '#ef444450' : 'rgba(255,255,255,0.08)') : 'rgba(255,255,255,0.1)'}`,
                color: '#fff', fontWeight: 700, fontSize: 'clamp(0.85rem,1.1vw,0.95rem)', cursor: sel === null ? 'pointer' : 'default', textAlign: 'left', lineHeight: 1.4 }}>
              {o}
            </motion.button>
          );
        })}
      </div>
      <AnimatePresence>
        {sel !== null && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto', marginTop: 12 }} exit={{ opacity: 0, height: 0 }}>
            <div style={{ padding: '12px 16px', borderRadius: 12, background: sel === current.correctIndex ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1.5px solid ${sel === current.correctIndex ? '#10b98130' : '#ef444430'}` }}>
              <p style={{ fontWeight: 700, fontSize: 'clamp(0.82rem,1vw,0.92rem)', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>{current.explanation}</p>
            </div>
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={handleNext}
              style={{ marginTop: 10, padding: '8px 24px', borderRadius: 20, background: `linear-gradient(135deg, ${t.acc}, ${t.acc2 || t.acc})`, border: 'none', color: '#fff', fontWeight: 800, fontSize: 'clamp(0.85rem,1.1vw,0.95rem)', cursor: 'pointer' }}>
              {isLast ? 'See Results →' : 'Next Scenario →'}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function MatchGame({ pairs, t }) {
  const [leftPick, setLeftPick] = useState(null);
  const [matched, setMatched] = useState({});
  const [wrong, setWrong] = useState(null);
  const shuffledRight = useMemo(() => [...pairs].sort(() => Math.random() - 0.5), [pairs]);
  const allDone = Object.keys(matched).length === pairs.length;

  const handleLeft = (i) => {
    if (matched[i] !== undefined) return;
    setLeftPick(i);
    setWrong(null);
  };

  const handleRight = (ri) => {
    if (leftPick === null) return;
    const rightPair = shuffledRight[ri];
    const leftPair = pairs[leftPick];
    if (rightPair.right === leftPair.right) {
      setMatched(prev => ({ ...prev, [leftPick]: ri }));
      setLeftPick(null);
      setWrong(null);
    } else {
      setWrong(ri);
      setTimeout(() => setWrong(null), 600);
    }
  };

  const compact = false;
  return (
    <div style={{ background: t.card, border: `2px solid ${t.cardBd}`, borderRadius: compact ? 14 : 18, padding: compact ? 'clamp(10px,1.8vw,18px)' : 'clamp(16px,2.5vw,28px)', backdropFilter: 'blur(8px)', width: '100%', maxWidth: 720, boxSizing: 'border-box', alignSelf: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: compact ? 8 : 14 }}>
        <span style={{ fontSize: compact ? 'clamp(0.9rem,1.3vw,1.1rem)' : 'clamp(1.2rem,2vw,1.6rem)' }}>🔗</span>
        <p style={{ fontWeight: 900, fontSize: compact ? 'clamp(0.82rem,1.1vw,0.92rem)' : 'clamp(0.95rem,1.3vw,1.1rem)', color: t.acc }}>Match the Concept!</p>
        <span style={{ marginLeft: 'auto', padding: '2px 10px', borderRadius: 10, background: `${t.acc}15`, fontWeight: 800, fontSize: 'clamp(0.68rem,0.85vw,0.78rem)', color: t.acc }}>{Object.keys(matched).length}/{pairs.length}</span>
      </div>
      {allDone ? (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: 'center', padding: compact ? '10px 0' : '16px 0' }}>
          <p style={{ fontSize: 'clamp(1.1rem,1.8vw,1.5rem)', fontWeight: 900, color: '#10b981' }}>All matched! Great job!</p>
        </motion.div>
      ) : (
        <p style={{ fontSize: 'clamp(0.7rem,0.9vw,0.8rem)', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: compact ? 6 : 12 }}>
          Tap a concept on the left, then its definition on the right.
        </p>
      )}
      {!allDone && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: compact ? 6 : 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 4 : 8 }}>
            {pairs.map((p, i) => {
              const isMatched = matched[i] !== undefined;
              const isActive = leftPick === i;
              return (
                <motion.button key={i} whileHover={!isMatched ? { scale: 1.02 } : {}} whileTap={!isMatched ? { scale: 0.98 } : {}} onClick={() => handleLeft(i)}
                  style={{ padding: compact ? 'clamp(6px,0.9vw,9px) clamp(8px,1vw,12px)' : 'clamp(10px,1.4vw,14px)', borderRadius: compact ? 9 : 12, background: isMatched ? 'rgba(16,185,129,0.12)' : isActive ? `${t.acc}20` : 'rgba(255,255,255,0.06)',
                    border: `1.5px solid ${isMatched ? '#10b98140' : isActive ? t.acc + '60' : 'rgba(255,255,255,0.1)'}`,
                    color: isMatched ? '#10b981' : '#fff', fontWeight: 800, fontSize: compact ? 'clamp(0.7rem,0.92vw,0.82rem)' : 'clamp(0.82rem,1.1vw,0.92rem)', cursor: isMatched ? 'default' : 'pointer', textAlign: 'left', lineHeight: 1.3,
                    opacity: isMatched ? 0.6 : 1, textDecoration: isMatched ? 'line-through' : 'none' }}>
                  {p.left}
                </motion.button>
              );
            })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 4 : 8 }}>
            {shuffledRight.map((p, ri) => {
              const isUsed = Object.values(matched).includes(ri);
              const isWrong = wrong === ri;
              return (
                <motion.button key={ri} whileHover={!isUsed ? { scale: 1.02 } : {}} whileTap={!isUsed ? { scale: 0.98 } : {}} onClick={() => handleRight(ri)}
                  animate={isWrong ? { x: [0, -6, 6, -4, 4, 0] } : {}} transition={isWrong ? { duration: 0.4 } : {}}
                  style={{ padding: compact ? 'clamp(6px,0.9vw,9px) clamp(8px,1vw,12px)' : 'clamp(10px,1.4vw,14px)', borderRadius: compact ? 9 : 12, background: isUsed ? 'rgba(16,185,129,0.12)' : isWrong ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.06)',
                    border: `1.5px solid ${isUsed ? '#10b98140' : isWrong ? '#ef444450' : 'rgba(255,255,255,0.1)'}`,
                    color: isUsed ? '#10b981' : '#fff', fontWeight: 700, fontSize: compact ? 'clamp(0.66rem,0.85vw,0.76rem)' : 'clamp(0.78rem,1vw,0.88rem)', cursor: isUsed ? 'default' : 'pointer', textAlign: 'left', lineHeight: 1.3,
                    opacity: isUsed ? 0.6 : 1, textDecoration: isUsed ? 'line-through' : 'none' }}>
                  {p.right}
                </motion.button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Mission: drag & drop sorter ─────────────────────────────────────────────
// slide.sorter = { intro, buckets: [{ id, label, hint }], items: [{ text, bucket }] }
// Flow: the student places EVERY card first (any bucket accepts it, a placed card can be tapped to take it
// back), then presses "Check my answers". Correct cards lock in green; wrong ones flash red, show their hint
// and return to the pile for another try. The mission is complete when a check finds every card correct.
function DragSorter({ data, t, onDone }) {
  const [placed, setPlaced] = useState({});   // itemIndex -> bucketId (student's choice, unverified)
  const [locked, setLocked] = useState({});   // itemIndex -> true once verified correct
  const [wrongSet, setWrongSet] = useState({}); // itemIndex -> true while flashing red after a check
  const [hints, setHints] = useState([]);     // hints of the last check's wrong cards
  const [attempts, setAttempts] = useState(0);
  const [selected, setSelected] = useState(null); // tap-to-place fallback
  const [hover, setHover] = useState(null);   // bucket id under the dragged chip
  const bucketRefs = useRef({});
  const total = data.items.length;
  const lockedCount = Object.keys(locked).length;
  const placedCount = Object.keys(placed).length;
  const allPlaced = placedCount === total;
  const allDone = lockedCount === total;
  const doneRef = useRef(false);
  useEffect(() => { if (allDone && !doneRef.current) { doneRef.current = true; onDone && onDone(); } }, [allDone, onDone]);

  const bucketAt = (x, y) => {
    for (const b of data.buckets) {
      const el = bucketRefs.current[b.id];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return b.id;
    }
    return null;
  };
  const place = (i, bucketId) => {
    if (!bucketId || locked[i]) return;
    setPlaced(p => ({ ...p, [i]: bucketId })); setSelected(null); setHints([]);
  };
  const takeBack = (i) => { if (locked[i]) return; setPlaced(p => { const n = { ...p }; delete n[i]; return n; }); setHints([]); };
  const check = () => {
    const newLocked = { ...locked }; const wrong = {}; const hs = [];
    data.items.forEach((it, i) => {
      if (locked[i]) return;
      if (placed[i] === it.bucket) newLocked[i] = true;
      else { wrong[i] = true; const b = data.buckets.find(x => x.id === it.bucket); hs.push(it.hint || (b && b.hint) || 'Read the situation again.'); }
    });
    setAttempts(a => a + 1); setLocked(newLocked); setWrongSet(wrong); setHints(hs);
    // wrong cards go back to the pile after the red flash
    setTimeout(() => { setPlaced(p => { const n = { ...p }; Object.keys(wrong).forEach(k => delete n[k]); return n; }); setWrongSet({}); }, 1100);
  };
  const chipFs = 'clamp(0.76rem,0.95vw,0.86rem)';
  const wrongCount = Object.keys(wrongSet).length;
  return (
    <div style={{ background: t.card, border: `2px solid ${t.cardBd}`, borderRadius: 16, padding: 'clamp(12px,1.6vw,20px)', backdropFilter: 'blur(8px)', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 'clamp(1rem,1.4vw,1.25rem)' }}>🎯</span>
        <p style={{ fontWeight: 800, fontSize: 'clamp(0.95rem,1.25vw,1.1rem)', color: t.acc, flex: 1 }}>{data.title || 'Mission: sort the traps'}</p>
        <span style={{ padding: '2px 10px', borderRadius: 10, background: `${t.acc}18`, border: `1px solid ${t.acc}40`, fontWeight: 800, fontSize: 'clamp(0.7rem,0.85vw,0.8rem)', color: t.acc }}>{allDone ? `${total}/${total} ✓` : attempts ? `${lockedCount}/${total} correct` : `${placedCount}/${total} placed`}</span>
      </div>
      {!allDone && <p style={{ fontSize: 'clamp(0.76rem,0.92vw,0.86rem)', fontWeight: 600, color: 'rgba(249,240,231,0.7)', marginBottom: 8 }}>{data.intro || 'Drag each card onto a group (or tap the card, then the group). Place them all, then press Check.'}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${data.buckets.length}, 1fr)`, gap: 8, marginBottom: 10 }}>
        {data.buckets.map(b => {
          const mine = data.items.map((it, i) => ({ it, i })).filter(({ i }) => placed[i] === b.id);
          const active = hover === b.id || (selected !== null);
          return (
            <div key={b.id} ref={el => { bucketRefs.current[b.id] = el; }} onClick={() => selected !== null && place(selected, b.id)}
              style={{ minHeight: 'clamp(52px,6vw,72px)', padding: '6px 8px', borderRadius: 12, background: hover === b.id ? `${t.acc}22` : 'rgba(255,255,255,0.05)', border: `2px dashed ${active ? t.acc : t.cardBd}`, transition: 'all 0.2s', cursor: selected !== null ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', gap: 5 }}>
              <p style={{ fontWeight: 800, fontSize: 'clamp(0.78rem,1vw,0.9rem)', color: BRAND.ivory, textAlign: 'center' }}>{b.label}</p>
              {mine.map(({ it, i }) => {
                const ok = !!locked[i]; const bad = !!wrongSet[i];
                return (
                  <motion.div key={i} initial={{ scale: 0.8, opacity: 0 }} animate={bad ? { scale: 1, opacity: 1, x: [0, -6, 6, -4, 4, 0] } : { scale: 1, opacity: 1 }} transition={bad ? { duration: 0.45 } : {}}
                    onClick={(e) => { e.stopPropagation(); takeBack(i); }} title={ok ? '' : 'Tap to take it back'}
                    style={{ padding: '5px 8px', borderRadius: 8, cursor: ok ? 'default' : 'pointer', fontWeight: 600, fontSize: 'clamp(0.68rem,0.85vw,0.78rem)', lineHeight: 1.3,
                      background: ok ? 'rgba(16,185,129,0.18)' : bad ? 'rgba(239,68,68,0.22)' : 'rgba(255,255,255,0.1)',
                      border: `1px solid ${ok ? 'rgba(16,185,129,0.5)' : bad ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.3)'}`,
                      color: ok ? '#c8ffe0' : bad ? '#fecaca' : BRAND.ivory }}>
                    {ok ? '✓ ' : bad ? '✗ ' : ''}{it.text}
                  </motion.div>
                );
              })}
            </div>
          );
        })}
      </div>
      {allDone && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ padding: 12, borderRadius: 12, background: 'rgba(16,185,129,0.14)', border: '1.5px solid rgba(16,185,129,0.45)', textAlign: 'center' }}>
          <p style={{ fontWeight: 900, color: '#6ee7b7', fontSize: 'clamp(1rem,1.4vw,1.2rem)' }}>{attempts === 1 ? 'Mission complete — all correct on the first try!' : `Mission complete — all correct after ${attempts} checks!`}</p>
          {data.outro && <p style={{ marginTop: 4, fontWeight: 600, color: 'rgba(249,240,231,0.85)', fontSize: 'clamp(0.8rem,1vw,0.9rem)' }}>{data.outro}</p>}
        </motion.div>
      )}
      {/* Draggable chips are hidden (not unmounted) once placed: framer-motion's drag feature registers each chip
          with the surrounding AnimatePresence and never unregisters on unmount, which would block the slide's
          exit animation (mode="wait") and freeze navigation after the first drop. */}
      <div style={{ display: allDone ? 'none' : 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, position: 'relative', zIndex: 5 }}>
        {data.items.map((it, i) => (
          <motion.div key={i} drag dragSnapToOrigin dragMomentum={false} dragElastic={0.2} whileDrag={{ scale: 1.05, zIndex: 50, boxShadow: '0 12px 30px rgba(0,0,0,0.5)' }}
            onDrag={(e, info) => setHover(bucketAt(info.point.x - window.scrollX, info.point.y - window.scrollY))}
            onDragEnd={(e, info) => { const b = bucketAt(info.point.x - window.scrollX, info.point.y - window.scrollY); setHover(null); place(i, b); }}
            onClick={() => setSelected(selected === i ? null : i)}
            style={{ padding: '6px 10px', borderRadius: 10, background: selected === i ? `${t.acc}30` : 'rgba(255,255,255,0.1)', border: `1.5px solid ${selected === i ? t.acc : 'rgba(255,255,255,0.25)'}`, color: BRAND.ivory, fontWeight: 600, fontSize: chipFs, lineHeight: 1.3, cursor: 'grab', userSelect: 'none', touchAction: 'none', maxWidth: '100%', display: placed[i] === undefined ? 'flex' : 'none', alignItems: 'center', gap: 6 }}>
            ✋ {it.text}
          </motion.div>
        ))}
      </div>
      {!allDone && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={check} disabled={!allPlaced || wrongCount > 0}
            style={{ padding: 'clamp(8px,1vw,11px) clamp(16px,2vw,24px)', borderRadius: 12, border: 'none', cursor: allPlaced && !wrongCount ? 'pointer' : 'not-allowed', fontWeight: 900, fontSize: 'clamp(0.82rem,1vw,0.92rem)', letterSpacing: '0.04em',
              background: allPlaced && !wrongCount ? t.acc : 'rgba(255,255,255,0.12)', color: allPlaced && !wrongCount ? BRAND.navy900 : 'rgba(249,240,231,0.45)', transition: 'all 0.2s' }}>
            {attempts ? 'Check again' : 'Check my answers'}
          </button>
          {!allPlaced && <span style={{ fontSize: 'clamp(0.72rem,0.88vw,0.8rem)', fontWeight: 600, color: 'rgba(249,240,231,0.55)' }}>Place all {total} cards to unlock the check.</span>}
        </div>
      )}
      <AnimatePresence>
        {hints.length > 0 && !allDone && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', color: '#fecaca', fontWeight: 600, fontSize: 'clamp(0.74rem,0.9vw,0.84rem)' }}>
            <p style={{ fontWeight: 800, marginBottom: 3 }}>{hints.length === 1 ? '1 card is in the wrong place — it went back to the pile.' : `${hints.length} cards are in the wrong place — they went back to the pile.`}</p>
            {[...new Set(hints)].map((h, k) => <p key={k}>💡 {h}</p>)}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Mission: password lab with crack-time estimate ──────────────────────────
const COMMON_PW = ['password', '123456', '12345678', 'qwerty', 'abc123', 'letmein', 'iloveyou', 'tim', 'admin', 'welcome', 'football', 'dragon', 'monkey', 'princess', 'sunshine', '000000', '111111'];
function crackTime(pw) {
  if (!pw) return { seconds: 0, label: '—', score: 0 };
  let charset = 0;
  if (/[a-z]/.test(pw)) charset += 26;
  if (/[A-Z]/.test(pw)) charset += 26;
  if (/[0-9]/.test(pw)) charset += 10;
  if (/[^a-zA-Z0-9]/.test(pw)) charset += 33;
  let bits = pw.length * Math.log2(charset || 1);
  const low = pw.toLowerCase();
  if (COMMON_PW.some(c => low.includes(c))) bits = Math.min(bits, 18);     // dictionary word inside
  if (/^(.)\1+$/.test(pw)) bits = Math.min(bits, 10);                       // aaaaaa
  if (/^(0123|1234|2345|3456|4567|5678|6789|abcd|qwer)/i.test(pw)) bits = Math.min(bits, 16);
  const guesses = Math.pow(2, bits) / 2;                                     // average case
  const seconds = guesses / 1e10;                                            // 10 billion guesses/s (offline attack)
  const units = [['seconds', 60], ['minutes', 60], ['hours', 24], ['days', 365], ['years', 100], ['centuries', 1e9]];
  let v = seconds, label = 'less than a second';
  const years = seconds / (60 * 60 * 24 * 365);
  if (years > 13.8e9) {
    label = 'longer than the universe has existed';
  } else if (years > 1e6) {
    label = `${Math.round(years / 1e6).toLocaleString()} million years`;
  } else if (seconds >= 1) {
    for (let k = 0; k < units.length; k++) {
      const [name, div] = units[k];
      if (v < div || k === units.length - 1) { label = `${v >= 1000 ? Math.round(v).toLocaleString() : Math.max(1, Math.round(v))} ${name}`; break; }
      v = v / div;
    }
  }
  const score = seconds < 1 ? 0 : seconds < 3600 ? 1 : years < 1 ? 2 : years < 100 ? 3 : 4;
  return { seconds, years, label, score, bits: Math.round(bits) };
}
// Flow: the student types a password (nothing is revealed while typing), presses "Test it" and only then
// discovers how long it would take to crack, what it already does well and what to change. Editing the
// password hides the verdict again until the next test. Win = 100+ years.
function PasswordLab({ t, onDone, opts }) {
  const o = opts && typeof opts === 'object' ? opts : {};
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [tested, setTested] = useState('');   // the password that was last tested ('' = no verdict shown)
  const [tries, setTries] = useState(0);
  const verdictFor = tested && tested === pw;
  const r = useMemo(() => crackTime(tested), [tested]);
  const colors = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#10b981'];
  const labels = ['Instant — a criminal is already in', 'Weak — cracked over a coffee break', 'Okay — but not for your important accounts', 'Strong — nearly there', 'Hero level — 100+ years'];
  const low = tested.toLowerCase();
  const checks = [
    { ok: tested.length >= 12, done: `${tested.length} characters — good length`, fix: tested.length < 8 ? `Only ${tested.length} characters: every extra character multiplies the work. Aim for 12 or more.` : `${tested.length} characters: make it 12 or more — four random words joined together get you there.` },
    { ok: /[a-z]/.test(tested) && /[A-Z]/.test(tested), done: 'Mixes UPPER and lower case', fix: 'Mix UPPER and lower case letters (e.g. capitalise one word in the middle).' },
    { ok: /[0-9]/.test(tested), done: 'Contains a number', fix: 'Add a number — but not your birth year, that is the first thing they try.' },
    { ok: /[^a-zA-Z0-9]/.test(tested), done: 'Contains a symbol', fix: 'Add a symbol (! ? # - …), for example between the words.' },
    { ok: tested.length > 0 && !COMMON_PW.some(c => low.includes(c)) && !/^(0123|1234|2345|3456|4567|5678|6789|abcd|qwer)/i.test(tested) && !/^(.)\1+$/.test(tested), done: 'No common words, names or keyboard patterns', fix: 'Remove common words, names and keyboard runs (1234, qwerty…): criminals test those first.' },
  ];
  const won = verdictFor && r.score === 4;
  const doneRef = useRef(false);
  useEffect(() => { if (won && !doneRef.current) { doneRef.current = true; onDone && onDone(); } }, [won, onDone]);
  const test = () => { if (!pw) return; setTested(pw); setTries(n => n + 1); };
  return (
    <div style={{ background: t.card, border: `2px solid ${t.cardBd}`, borderRadius: 16, padding: 'clamp(12px,1.6vw,20px)', backdropFilter: 'blur(8px)', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 'clamp(1rem,1.4vw,1.25rem)' }}>🔐</span>
        <p style={{ fontWeight: 800, fontSize: 'clamp(0.95rem,1.25vw,1.1rem)', color: t.acc, flex: 1 }}>{o.title || 'Mission: build a password that takes centuries to crack'}</p>
        {tries > 0 && <span style={{ padding: '2px 10px', borderRadius: 10, background: `${t.acc}18`, border: `1px solid ${t.acc}40`, fontWeight: 800, fontSize: 'clamp(0.7rem,0.85vw,0.8rem)', color: t.acc }}>{tries} {tries === 1 ? 'test' : 'tests'}</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input value={pw} onChange={e => setPw(e.target.value.slice(0, 40))} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); test(); } }} type={show ? 'text' : 'password'} placeholder="Type a password to test (not your real one — it stays on this screen)" autoComplete="off" spellCheck={false}
          style={{ flex: '1 1 220px', padding: 'clamp(10px,1.2vw,14px)', borderRadius: 12, background: 'rgba(255,255,255,0.08)', border: `2px solid ${t.cardBd}`, color: BRAND.ivory, fontSize: 'clamp(0.95rem,1.2vw,1.1rem)', fontWeight: 700, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box', letterSpacing: 1, minWidth: 0 }} />
        <button type="button" onClick={() => setShow(s => !s)} style={{ padding: '0 14px', borderRadius: 12, border: `1.5px solid ${t.cardBd}`, background: 'rgba(255,255,255,0.06)', color: BRAND.ivory, fontWeight: 700, cursor: 'pointer', fontSize: 'clamp(0.75rem,0.9vw,0.85rem)' }}>{show ? 'Hide' : 'Show'}</button>
        <button type="button" onClick={test} disabled={!pw || verdictFor}
          style={{ padding: '0 clamp(16px,2vw,24px)', borderRadius: 12, border: 'none', cursor: pw && !verdictFor ? 'pointer' : 'not-allowed', fontWeight: 900, fontSize: 'clamp(0.82rem,1vw,0.92rem)', letterSpacing: '0.04em', background: pw && !verdictFor ? t.acc : 'rgba(255,255,255,0.12)', color: pw && !verdictFor ? BRAND.navy900 : 'rgba(249,240,231,0.45)', transition: 'all 0.2s' }}>
          {tries ? 'Test it again' : 'Test it'}
        </button>
      </div>
      {!verdictFor && (
        <p style={{ marginTop: 8, fontWeight: 600, color: 'rgba(249,240,231,0.6)', fontSize: 'clamp(0.74rem,0.9vw,0.84rem)' }}>
          {tries ? 'Edit your password, then press "Test it again" to see the new verdict.' : 'Type a password, then press "Test it" to discover how long a criminal\'s computer would need to guess it.'}
        </p>
      )}
      <AnimatePresence>
        {verdictFor && (
          <motion.div key={tested} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <div style={{ marginTop: 10, height: 10, borderRadius: 5, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
              <motion.div initial={{ width: '0%' }} animate={{ width: `${(r.score + 1) * 20}%`, background: colors[r.score] }} transition={{ duration: 0.6 }} style={{ height: '100%', borderRadius: 5 }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 6, gap: 10, flexWrap: 'wrap' }}>
              <p style={{ fontWeight: 800, color: colors[r.score], fontSize: 'clamp(0.85rem,1.05vw,0.95rem)' }}>{labels[r.score]}</p>
              <p style={{ fontWeight: 700, color: BRAND.ivory, fontSize: 'clamp(0.82rem,1vw,0.92rem)' }}>Time to crack: <span style={{ color: t.acc, fontWeight: 900 }}>{r.label}</span></p>
            </div>
            {won ? (
              <div style={{ marginTop: 10, padding: 10, borderRadius: 12, background: 'rgba(16,185,129,0.14)', border: '1.5px solid rgba(16,185,129,0.45)' }}>
                <p style={{ fontWeight: 900, color: '#6ee7b7', fontSize: 'clamp(0.95rem,1.2vw,1.1rem)' }}>{o.win || 'Hero level! Even a supercomputer would give up.'}</p>
                <p style={{ marginTop: 3, fontWeight: 600, color: 'rgba(249,240,231,0.85)', fontSize: 'clamp(0.76rem,0.95vw,0.86rem)' }}>
                  {tries === 1 ? 'First try! ' : `It took you ${tries} tests. `}Remember: a passphrase like <span style={{ fontFamily: 'monospace', color: t.acc }}>Purple-Tiger-Eats-42-Pizzas!</span> is long, easy to remember and impossible to guess. Never reuse it on two sites.
                </p>
              </div>
            ) : (
              <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: `1px solid ${t.cardBd}` }}>
                <p style={{ fontWeight: 800, fontSize: 'clamp(0.76rem,0.92vw,0.86rem)', color: BRAND.ivory, marginBottom: 4 }}>How to make it stronger:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {checks.map((c, i) => (
                    <p key={i} style={{ fontSize: 'clamp(0.72rem,0.88vw,0.82rem)', fontWeight: 600, color: c.ok ? '#6ee7b7' : '#fde68a', lineHeight: 1.35 }}>{c.ok ? '✓ ' : '→ '}{c.ok ? c.done : c.fix}</p>
                  ))}
                  {checks.every(c => c.ok) && <p style={{ fontSize: 'clamp(0.72rem,0.88vw,0.82rem)', fontWeight: 600, color: '#fde68a' }}>→ Everything is there — just make it longer. Each extra character multiplies the time to crack.</p>}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Mission: clean Tim's phone (safe-settings toggles) ──────────────────────
// slide.phone = { title, items: [{ label, desc, safeOn: bool, initial: bool, why }] }
// Flow: switches start in Tim's (partly wrong) state and give no colour feedback while the student decides.
// "Check settings" then marks each line safe (green, with the reason) or unsafe (red, with the reason to fix it);
// the student can keep flipping switches and check again. Complete when a check finds every setting safe.
function PhoneCleanup({ data, t, onDone }) {
  const [state, setState] = useState(() => data.items.map(it => !!it.initial));
  const [result, setResult] = useState(null); // array of booleans from the last check, or null
  const [attempts, setAttempts] = useState(0);
  const safeNow = data.items.map((it, i) => state[i] === !!it.safeOn);
  const allSafe = !!result && result.every(Boolean);
  const safeCount = result ? result.filter(Boolean).length : 0;
  const doneRef = useRef(false);
  useEffect(() => { if (allSafe && !doneRef.current) { doneRef.current = true; onDone && onDone(); } }, [allSafe, onDone]);
  const flip = (i) => { if (allSafe) return; setState(s => s.map((v, k) => k === i ? !v : v)); setResult(null); };
  const check = () => { setResult(safeNow); setAttempts(a => a + 1); };
  const dirty = result === null;
  return (
    <div style={{ background: t.card, border: `2px solid ${t.cardBd}`, borderRadius: 16, padding: 'clamp(12px,1.6vw,20px)', backdropFilter: 'blur(8px)', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 'clamp(1rem,1.4vw,1.25rem)' }}>{data.icon || '📱'}</span>
        <p style={{ fontWeight: 800, fontSize: 'clamp(0.95rem,1.25vw,1.1rem)', color: t.acc, flex: 1 }}>{data.title || "Mission: clean up Tim's phone"}</p>
        {result && <span style={{ padding: '2px 10px', borderRadius: 10, background: allSafe ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.15)', border: `1px solid ${allSafe ? '#10b981' : 'rgba(239,68,68,0.5)'}`, fontWeight: 800, fontSize: 'clamp(0.7rem,0.85vw,0.8rem)', color: allSafe ? '#6ee7b7' : '#fecaca' }}>{safeCount}/{data.items.length} safe</span>}
      </div>
      {!allSafe && <p style={{ fontSize: 'clamp(0.78rem,0.95vw,0.88rem)', fontWeight: 600, color: 'rgba(249,240,231,0.7)', marginBottom: 8 }}>{data.intro || 'Some of these settings are wrong. Set every switch the way you think is safe, then press Check.'}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.items.map((it, i) => {
          const on = state[i];
          const verdict = result ? result[i] : null; // true / false / null (not checked yet)
          const bg = verdict === true ? 'rgba(16,185,129,0.1)' : verdict === false ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.05)';
          const bd = verdict === true ? 'rgba(16,185,129,0.4)' : verdict === false ? 'rgba(239,68,68,0.45)' : t.cardBd;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 'clamp(6px,0.8vw,9px) clamp(10px,1.1vw,12px)', borderRadius: 11, background: bg, border: `1.5px solid ${bd}`, transition: 'all 0.25s' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontWeight: 800, fontSize: 'clamp(0.82rem,1.02vw,0.94rem)', color: BRAND.ivory, lineHeight: 1.25 }}>{verdict === true ? '✓ ' : verdict === false ? '✗ ' : ''}{it.label}</p>
                <p style={{ fontWeight: 600, fontSize: 'clamp(0.7rem,0.86vw,0.8rem)', color: verdict === true ? '#a7f3d0' : verdict === false ? '#fecaca' : 'rgba(249,240,231,0.68)', lineHeight: 1.3, marginTop: 1 }}>
                  {verdict === null ? it.desc : verdict ? (it.why || 'Safe.') : `Not safe. ${it.why || 'Flip it and check again.'}`}
                </p>
              </div>
              <button type="button" role="switch" aria-checked={on} onClick={() => flip(i)} disabled={allSafe}
                style={{ width: 46, height: 26, borderRadius: 13, border: 'none', background: on ? t.acc : 'rgba(255,255,255,0.2)', position: 'relative', cursor: allSafe ? 'default' : 'pointer', flexShrink: 0, transition: 'background 0.25s' }}>
                <span style={{ position: 'absolute', top: 3, left: on ? 23 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />
              </button>
              <span style={{ width: 28, fontSize: 'clamp(0.66rem,0.8vw,0.74rem)', fontWeight: 800, color: on ? BRAND.ivory : 'rgba(249,240,231,0.5)' }}>{on ? 'ON' : 'OFF'}</span>
            </div>
          );
        })}
      </div>
      {!allSafe && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={check} disabled={!dirty}
            style={{ padding: 'clamp(8px,1vw,11px) clamp(16px,2vw,24px)', borderRadius: 12, border: 'none', cursor: dirty ? 'pointer' : 'not-allowed', fontWeight: 900, fontSize: 'clamp(0.82rem,1vw,0.92rem)', letterSpacing: '0.04em', background: dirty ? t.acc : 'rgba(255,255,255,0.12)', color: dirty ? BRAND.navy900 : 'rgba(249,240,231,0.45)', transition: 'all 0.2s' }}>
            {attempts ? 'Check again' : 'Check settings'}
          </button>
          {result && !allSafe && <span style={{ fontSize: 'clamp(0.74rem,0.9vw,0.82rem)', fontWeight: 700, color: '#fecaca' }}>{data.items.length - safeCount} {data.items.length - safeCount === 1 ? 'setting is' : 'settings are'} still unsafe — read the red lines, flip, and check again.</span>}
        </div>
      )}
      <AnimatePresence>
        {allSafe && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: 10, padding: 10, borderRadius: 12, background: 'rgba(16,185,129,0.14)', border: '1.5px solid rgba(16,185,129,0.45)', textAlign: 'center' }}>
            <p style={{ fontWeight: 900, color: '#6ee7b7', fontSize: 'clamp(0.95rem,1.2vw,1.1rem)' }}>{data.outro || "Tim's phone is clean! Tonight, check your own — with a parent next to you."}</p>
            <p style={{ marginTop: 3, fontWeight: 600, color: 'rgba(249,240,231,0.8)', fontSize: 'clamp(0.76rem,0.95vw,0.86rem)' }}>{attempts === 1 ? 'All safe on the first check.' : `All safe after ${attempts} checks.`}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Branching story: choose the move, see the consequence, go back ──────────
// slide.branch = { start, nodes: { id: { who, text, options: [{ text, next, tone }], end, outcome: 'good'|'bad', lesson } } }
function Branch({ data, t, onDone }) {
  const [path, setPath] = useState([data.start]);
  const cur = data.nodes[path[path.length - 1]];
  const doneRef = useRef(false);
  useEffect(() => { if (cur && cur.end && cur.outcome === 'good' && !doneRef.current) { doneRef.current = true; onDone && onDone(); } }, [cur, onDone]);
  if (!cur) return null;
  const who = CAST[cur.who] || null;
  const tone = { good: { bg: 'rgba(16,185,129,0.14)', bd: 'rgba(16,185,129,0.5)', c: '#6ee7b7', tag: 'Safe ending' }, bad: { bg: 'rgba(239,68,68,0.12)', bd: 'rgba(239,68,68,0.45)', c: '#fca5a5', tag: 'Ouch — consequence' } };
  const end = cur.end ? tone[cur.outcome] || tone.bad : null;
  return (
    <div style={{ background: t.card, border: `2px solid ${t.cardBd}`, borderRadius: 16, padding: 'clamp(12px,1.6vw,20px)', backdropFilter: 'blur(8px)', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 'clamp(1rem,1.4vw,1.25rem)' }}>🧭</span>
        <p style={{ fontWeight: 800, fontSize: 'clamp(0.95rem,1.25vw,1.1rem)', color: t.acc, flex: 1 }}>{data.title || 'Choose the move'}</p>
        <div style={{ display: 'flex', gap: 4 }}>{path.map((_, i) => <span key={i} style={{ width: 8, height: 8, borderRadius: 4, background: i === path.length - 1 ? t.acc : `${t.acc}55` }} />)}</div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={path.join('>')} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 'clamp(9px,1.1vw,12px)', borderRadius: 12, background: end ? end.bg : 'rgba(255,255,255,0.06)', border: `1.5px solid ${end ? end.bd : 'rgba(255,255,255,0.14)'}` }}>
            {who && (
              <div style={{ width: 44, height: 44, borderRadius: '50%', border: `2px solid ${t.acc}`, background: BRAND.navy700, overflow: 'hidden', flexShrink: 0 }}>
                <img src={castImg(cur.who)} alt={who.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transform: 'scale(1.35)', transformOrigin: 'top center' }} />
              </div>
            )}
            <div style={{ flex: 1 }}>
              {end && <p style={{ fontWeight: 900, fontSize: 'clamp(0.72rem,0.88vw,0.8rem)', color: end.c, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>{end.tag}</p>}
              {who && !end && <p style={{ fontWeight: 800, fontSize: 'clamp(0.66rem,0.82vw,0.74rem)', color: t.acc, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>{who.name}</p>}
              <p style={{ fontWeight: 600, fontSize: 'clamp(0.88rem,1.1vw,1rem)', color: BRAND.ivory, lineHeight: 1.45 }}>{cur.text}</p>
              {end && cur.lesson && <p style={{ marginTop: 6, fontWeight: 700, fontSize: 'clamp(0.78rem,0.95vw,0.88rem)', color: end.c, lineHeight: 1.4 }}>→ {cur.lesson}</p>}
            </div>
          </div>
          {!end && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {cur.options.map((o, i) => (
                <motion.button key={i} type="button" whileHover={{ scale: 1.01, x: 3 }} whileTap={{ scale: 0.99 }} onClick={() => setPath(p => [...p, o.next])}
                  style={{ padding: 'clamp(8px,1.1vw,11px) clamp(12px,1.4vw,16px)', borderRadius: 11, background: 'rgba(255,255,255,0.08)', border: `1.5px solid ${t.cardBd}`, color: BRAND.ivory, fontWeight: 700, fontSize: 'clamp(0.84rem,1.02vw,0.94rem)', textAlign: 'left', cursor: 'pointer', lineHeight: 1.35, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ width: 24, height: 24, borderRadius: 7, background: `${t.acc}25`, border: `1.5px solid ${t.acc}60`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem', color: t.acc, flexShrink: 0 }}>{String.fromCharCode(65 + i)}</span>
                  <span style={{ flex: 1 }}>{o.text}</span>
                </motion.button>
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        {path.length > 1 && (
          <button type="button" onClick={() => setPath(p => p.slice(0, -1))} style={{ padding: '7px 14px', borderRadius: 18, border: `1.5px solid ${t.cardBd}`, background: 'rgba(255,255,255,0.06)', color: BRAND.ivory, fontWeight: 700, fontSize: 'clamp(0.76rem,0.92vw,0.86rem)', cursor: 'pointer' }}>← Go back one step</button>
        )}
        {end && (
          <button type="button" onClick={() => setPath([data.start])} style={{ padding: '7px 14px', borderRadius: 18, border: 'none', background: `linear-gradient(135deg, ${t.acc}, ${t.acc2})`, color: BRAND.navy900, fontWeight: 800, fontSize: 'clamp(0.76rem,0.92vw,0.86rem)', cursor: 'pointer' }}>Start over</button>
        )}
      </div>
    </div>
  );
}

// ─── Badge toast ─────────────────────────────────────────────────────────────
function BadgeToast({ award, onClose }) {
  useEffect(() => { const id = setTimeout(onClose, 7000); return () => clearTimeout(id); }, [onClose]);
  return (
    <motion.div initial={{ opacity: 0, y: 40, scale: 0.9, x: '-50%' }} animate={{ opacity: 1, y: 0, scale: 1, x: '-50%' }} exit={{ opacity: 0, y: 20, scale: 0.95, x: '-50%' }} transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      onClick={onClose} role="status"
      style={{ position: 'absolute', left: '50%', bottom: 26, zIndex: 60, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px 12px 14px', borderRadius: 18, background: `linear-gradient(135deg, ${BRAND.navy700}, ${BRAND.navy900})`, border: `2px solid ${BRAND.goldLight}`, boxShadow: '0 14px 40px rgba(0,0,0,0.55), 0 0 30px rgba(226,184,120,0.25)', cursor: 'pointer', fontFamily: BRAND.font }}>
      <motion.span animate={{ rotate: [0, -12, 12, -6, 6, 0], scale: [1, 1.15, 1] }} transition={{ duration: 0.9, delay: 0.2 }}
        style={{ width: 52, height: 52, borderRadius: '50%', background: `radial-gradient(circle at 35% 30%, ${BRAND.goldPale}, ${BRAND.gold})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, boxShadow: `0 0 0 3px ${BRAND.navy900}, 0 0 0 5px ${BRAND.gold}` }}>{award.icon || '🏅'}</motion.span>
      <div>
        <p style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: BRAND.goldLight }}>Badge earned</p>
        <p style={{ fontSize: '1.05rem', fontWeight: 900, color: BRAND.ivory, lineHeight: 1.15 }}>{award.name}</p>
        {award.desc && <p style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(249,240,231,0.75)', marginTop: 2 }}>{award.desc}</p>}
      </div>
    </motion.div>
  );
}

function useContentDensity(slide) {
  return useMemo(() => {
    const hasInteractive = !!(slide.quiz || slide.cards || slide.cia || slide.rules || slide.encrypt || slide.scenario || slide.match || slide.sorter || slide.passwordLab || slide.phone || slide.branch);
    if (hasInteractive) return 'heavy';
    const titleLen = slide.title ? slide.title.length : 0;
    const subLen = slide.sub ? slide.sub.length : 0;
    const bulletCount = slide.bullets ? slide.bullets.length : 0;
    const gridCount = (slide.grid ? slide.grid.length : 0) + (slide.recapItems ? slide.recapItems.length : 0) + (slide.glossary ? 1 : 0) + (slide.homeTask ? 1 : 0);
    const bulletCount2 = bulletCount + (slide.objectiveItems ? slide.objectiveItems.length : 0);
    const noteLen = slide.note ? slide.note.length : 0;
    const quoteLen = slide.quote ? slide.quote.length : 0;
    const sayLen = slide.say ? slide.say.reduce((a, s) => a + (s.text ? s.text.length : 0) + 40, 0) : 0;
    const total = titleLen + subLen + quoteLen + noteLen + sayLen + bulletCount2 * 50 + gridCount * 80;
    if (total < 120) return 'minimal';
    if (total < 250) return 'light';
    if (total < 450) return 'medium';
    return 'heavy';
  }, [slide]);
}

const SCALE = {
  minimal: { title: 'clamp(2rem,4vw,3rem)', sub: 'clamp(1.15rem,2vw,1.5rem)', bullet: 'clamp(1rem,1.6vw,1.2rem)', badge: 'clamp(0.82rem,1.1vw,0.95rem)', note: 'clamp(0.85rem,1.1vw,0.95rem)', pad: 'clamp(40px,5vw,72px) clamp(32px,4vw,56px)', gap: 20 },
  light:   { title: 'clamp(1.7rem,3.4vw,2.6rem)', sub: 'clamp(1.05rem,1.7vw,1.3rem)', bullet: 'clamp(0.95rem,1.4vw,1.1rem)', badge: 'clamp(0.78rem,1vw,0.9rem)', note: 'clamp(0.82rem,1vw,0.9rem)', pad: 'clamp(36px,4.5vw,64px) clamp(28px,3.5vw,48px)', gap: 18 },
  medium:  { title: 'clamp(1.3rem,2.8vw,2.2rem)', sub: 'clamp(0.88rem,1.3vw,1.05rem)', bullet: 'clamp(0.82rem,1.1vw,0.95rem)', badge: 'clamp(0.7rem,0.9vw,0.82rem)', note: 'clamp(0.75rem,0.9vw,0.85rem)', pad: 'clamp(24px,3.5vw,48px) clamp(24px,3vw,40px)', gap: 14 },
  heavy:   { title: 'clamp(1.3rem,2.8vw,2.2rem)', sub: 'clamp(0.88rem,1.3vw,1.05rem)', bullet: 'clamp(0.82rem,1.1vw,0.95rem)', badge: 'clamp(0.7rem,0.9vw,0.82rem)', note: 'clamp(0.75rem,0.9vw,0.85rem)', pad: 'clamp(24px,3.5vw,48px) clamp(24px,3vw,40px)', gap: 14 },
};
// Scene layout (V2 lessons): the text panel is wider, so type can be larger and still fit.
const SCENE_SCALE = {
  minimal: SCALE.minimal,
  light:   { ...SCALE.light, title: 'clamp(1.8rem,3.4vw,2.7rem)', sub: 'clamp(1.1rem,1.6vw,1.35rem)', bullet: 'clamp(1rem,1.4vw,1.15rem)' },
  medium:  { ...SCALE.medium, title: 'clamp(1.5rem,2.8vw,2.3rem)', sub: 'clamp(1rem,1.35vw,1.18rem)', bullet: 'clamp(0.92rem,1.15vw,1.04rem)', gap: 14 },
  heavy:   { ...SCALE.heavy, title: 'clamp(1.4rem,2.6vw,2.1rem)', sub: 'clamp(0.95rem,1.25vw,1.1rem)', bullet: 'clamp(0.88rem,1.1vw,1rem)', gap: 12 },
};

const CHALK_PAD = '5.5% 6.5%';
const CHALK_SCALE = {
  minimal: { title: 'clamp(2.8rem,5.5vw,4rem)', sub: 'clamp(1.5rem,2.8vw,2rem)', bullet: 'clamp(1.3rem,2.2vw,1.6rem)', badge: 'clamp(1rem,1.3vw,1.15rem)', note: 'clamp(1.05rem,1.3vw,1.15rem)', gap: 20 },
  light:   { title: 'clamp(2.3rem,4.8vw,3.5rem)', sub: 'clamp(1.35rem,2.3vw,1.7rem)', bullet: 'clamp(1.2rem,1.9vw,1.45rem)', badge: 'clamp(0.92rem,1.2vw,1.05rem)', note: 'clamp(0.98rem,1.2vw,1.08rem)', gap: 18 },
  medium:  { title: 'clamp(1.8rem,3.8vw,2.8rem)', sub: 'clamp(1.15rem,1.8vw,1.4rem)', bullet: 'clamp(1.05rem,1.6vw,1.25rem)', badge: 'clamp(0.85rem,1.1vw,0.98rem)', note: 'clamp(0.92rem,1.1vw,1rem)', gap: 14 },
  heavy:   { title: 'clamp(1.65rem,3.5vw,2.6rem)', sub: 'clamp(1.08rem,1.6vw,1.3rem)', bullet: 'clamp(1rem,1.45vw,1.18rem)', badge: 'clamp(0.8rem,1vw,0.92rem)', note: 'clamp(0.88rem,1.05vw,0.96rem)', gap: 12 },
};

function Slide({ slide, t, num, total, onBack, backLabel = 'Back to Lessons', onMission, onFamilyPrint, cover }) {
  const [imgHover, setImgHover] = React.useState(false);
  const narrow = useNarrow();
  const isCenter = slide.layout === 'center';
  const isSplit = slide.layout === 'split';
  const isCover = slide.layout === 'cover';
  const isThankYou = slide.layout === 'thankyou';
  const isVideo = slide.layout === 'video';
  const isScene = slide.layout === 'scene';
  const isChalkBg = slide.bgImg && slide.bgImg.includes('chalkboard');
  const isBoardBg = !!(slide.bgImg && slide.bgImg.includes('lesson-end'));
  const density = useContentDensity(slide);
  // a 'scene' with only badge + title + sub is a chapter cover: use the biggest type scale
  const isChapterCover = isCoverSlide(slide);
  const sc = isChapterCover ? SCALE.minimal : (isScene ? SCENE_SCALE : SCALE)[density];
  const sceneTint = useImageTint(isScene ? slide.img : isThankYou ? slide.sceneImg : null);

  if (isThankYou) {
    const sImg = slide.sceneImg; // illustration on the left + tinted panel (same look as the 'scene' layout)
    const pc = panelColors(sceneTint, slide.panelHsl);
    const imgW = slide.sceneImgWidth || 50;
    return (
      <div style={{ width: '100%', height: '100%', background: sImg ? pc.solid : slide.bgImg ? 'transparent' : t.bg, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: sImg && slide.textPos === 'bottom' ? 'flex-end' : 'center', justifyContent: sImg && slide.textPos !== 'bottom' ? 'flex-end' : sImg ? 'flex-start' : 'center' }}>
        {sImg && (
          <>
            <FullBleed src={sImg} imgPos={slide.imgPos} pos={slide.textPos} />
          </>
        )}
        {!sImg && slide.bgImg ? (
          <>
            <img src={slide.bgImg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,10,30,0.3)', zIndex: 0 }} />
          </>
        ) : (
          <>
            <div style={{ position: 'absolute', top: '-10%', left: '-5%', width: '35%', height: '35%', borderRadius: '50%', background: 'rgba(108,99,255,0.08)', filter: 'blur(60px)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-10%', right: '-5%', width: '30%', height: '30%', borderRadius: '50%', background: 'rgba(255,101,132,0.06)', filter: 'blur(50px)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', top: '30%', right: '15%', width: '20%', height: '20%', borderRadius: '50%', background: 'rgba(139,131,255,0.06)', filter: 'blur(40px)', pointerEvents: 'none' }} />
          </>
        )}
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
          style={sImg
            ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', padding: 'clamp(24px,3vw,44px) clamp(24px,3vw,44px) clamp(24px,3vw,44px) clamp(28px,4vw,64px)', width: slide.textPos === 'bottom' ? 'min(100%, 820px)' : `${100 - imgW}%`, boxSizing: 'border-box', position: 'relative', zIndex: 1 }
            : { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: 'clamp(24px,4vw,48px)', maxWidth: 600, position: 'relative', zIndex: 1 }}>
          {slide.img && (
            <motion.img src={slide.img} alt="" initial={{ y: 20 }} animate={{ y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              style={{ width: 'clamp(140px,20vw,220px)', height: 'auto', objectFit: 'contain', marginBottom: 'clamp(20px,3vw,36px)', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.3))' }} />
          )}
          <motion.h2 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            style={{ fontSize: 'clamp(1.8rem,4vw,2.8rem)', fontWeight: 900, lineHeight: 1.1, marginBottom: 16, background: sImg ? `linear-gradient(135deg, ${BRAND.ivory} 0%, ${BRAND.goldLight} 55%, ${BRAND.gold} 100%)` : 'linear-gradient(135deg, #6C63FF, #8B83FF, #C084FC)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {slide.title}
          </motion.h2>
          {slide.sub && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              style={{ fontSize: 'clamp(1rem,1.6vw,1.2rem)', fontWeight: 600, color: sImg ? 'rgba(249,240,231,0.85)' : 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: 'clamp(24px,3vw,40px)' }}>
              {slide.sub}
            </motion.p>
          )}
          {slide.badgesEarned && slide.badgesEarned.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 'clamp(18px,2.5vw,30px)' }}>
              {slide.badgesEarned.map((b, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px 6px 6px', borderRadius: 999, background: 'rgba(7,20,45,0.6)', border: `1.5px solid ${BRAND.goldLight}66`, color: BRAND.ivory, fontWeight: 700, fontSize: 'clamp(0.74rem,0.9vw,0.84rem)' }}>
                  <span style={{ width: 24, height: 24, borderRadius: '50%', background: `radial-gradient(circle at 35% 30%, ${BRAND.goldPale}, ${BRAND.gold})`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>{b.icon}</span>{b.name}
                </span>
              ))}
            </motion.div>
          )}
          {onBack && (
            <motion.button type="button" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              onClick={onBack}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 36px', borderRadius: 28, background: sImg ? `linear-gradient(135deg, ${BRAND.goldLight}, ${BRAND.gold})` : 'linear-gradient(135deg, #6C63FF, #8B83FF)', color: sImg ? BRAND.navy900 : '#fff', fontWeight: 900, fontSize: 'clamp(1rem,1.4vw,1.15rem)', border: 'none', cursor: 'pointer', boxShadow: sImg ? '0 6px 28px rgba(205,155,80,0.45)' : '0 6px 28px rgba(108,99,255,0.5)', letterSpacing: '0.02em', transition: 'transform 0.2s, box-shadow 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
              <FiArrowRight size={18} style={{ transform: 'rotate(180deg)' }} /> {backLabel}
            </motion.button>
          )}
        </motion.div>
        <CastFigures cast={slide.cast} />
      </div>
    );
  }

  if (isVideo) {
    // CORE brand decks (TECH1) get the navy/gold treatment; CYBER1 keeps the legacy purple look.
    const core = t === THEME_CORE;
    const titleGrad = core ? `linear-gradient(135deg, ${BRAND.ivory} 0%, ${BRAND.goldLight} 55%, ${BRAND.gold} 100%)` : 'linear-gradient(135deg, #6C63FF, #8B83FF, #C084FC)';
    return (
      <div style={{ width: '100%', height: '100%', background: core ? t.bg : '#0a0a1e', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', top: '-10%', left: '-5%', width: '35%', height: '35%', borderRadius: '50%', background: core ? 'rgba(205,155,80,0.10)' : 'rgba(108,99,255,0.06)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-10%', right: '-5%', width: '30%', height: '30%', borderRadius: '50%', background: core ? 'rgba(249,240,231,0.05)' : 'rgba(255,101,132,0.04)', filter: 'blur(50px)', pointerEvents: 'none' }} />
        {core && <img src={BRAND.owl} alt="" aria-hidden style={{ position: 'absolute', right: '-4%', bottom: '-8%', width: '34%', opacity: 0.06, pointerEvents: 'none' }} />}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', width: '100%', maxWidth: '85%', position: 'relative', zIndex: 1 }}>
          {slide.title && (
            <h2 style={{ fontSize: 'clamp(1.4rem,3vw,2.2rem)', fontWeight: 900, color: '#fff', marginBottom: 6, background: titleGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {slide.title}
            </h2>
          )}
          {slide.sub && (
            <p style={{ fontSize: 'clamp(0.85rem,1.3vw,1.05rem)', fontWeight: 600, color: core ? 'rgba(249,240,231,0.72)' : 'rgba(255,255,255,0.5)', marginBottom: 'clamp(14px,2vw,24px)', lineHeight: 1.5 }}>
              {slide.sub}
            </p>
          )}
          <div style={{ width: '100%', maxWidth: 900, borderRadius: 18, overflow: 'hidden', border: core ? `2px solid ${BRAND.gold}55` : '2px solid rgba(108,99,255,0.25)', boxShadow: core ? '0 8px 40px rgba(0,0,0,0.5), 0 0 30px rgba(205,155,80,0.14)' : '0 8px 40px rgba(0,0,0,0.5), 0 0 30px rgba(108,99,255,0.1)', background: '#000' }}>
            <video
              controls
              controlsList="nodownload"
              preload="metadata"
              playsInline
              style={{ width: '100%', height: 'auto', display: 'block', maxHeight: '65vh' }}
              poster="">
              <source src={slide.videoUrl} type="video/mp4" />
            </video>
          </div>
        </motion.div>
      </div>
    );
  }

  if (slide.layout === 'moduleTitle') {
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
        {slide.bgImg && (
          <>
            <img src={slide.bgImg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 0 }} />
          </>
        )}
        <div style={{ position: 'absolute', top: '-15%', left: '-10%', width: '50%', height: '50%', borderRadius: '50%', background: `${t.acc}10`, filter: 'blur(80px)', pointerEvents: 'none', zIndex: 1 }} />
        <div style={{ position: 'absolute', bottom: '-15%', right: '-10%', width: '45%', height: '45%', borderRadius: '50%', background: `${t.acc2 || t.acc}08`, filter: 'blur(70px)', pointerEvents: 'none', zIndex: 1 }} />
        <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 'clamp(32px,5vw,64px)', boxSizing: 'border-box' }}>
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: 680 }}>
            {slide.badge && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                style={{ display: 'inline-block', padding: '8px 24px', borderRadius: 24, background: `linear-gradient(135deg, ${t.acc}35, ${t.acc2 || t.acc}22)`, border: `2px solid ${t.acc}50`, fontWeight: 900, fontSize: 'clamp(0.75rem,1.1vw,0.9rem)', color: t.acc, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 24, backdropFilter: 'blur(12px)', boxShadow: `0 4px 24px ${t.acc}25` }}>
                {slide.badge}
              </motion.span>
            )}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              style={{ width: 60, height: 4, borderRadius: 2, background: `linear-gradient(90deg, ${t.acc}, ${t.acc2 || t.acc})`, marginBottom: 28 }}
            />
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.5 }}
              style={{ fontSize: 'clamp(2rem,4.5vw,3.2rem)', fontWeight: 900, lineHeight: 1.1, marginBottom: 20, background: `linear-gradient(135deg, #fff 0%, ${t.acc} 50%, ${t.acc2 || t.acc} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))' }}>
              {slide.title}
            </motion.h1>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              style={{ width: 120, height: 3, borderRadius: 2, background: `linear-gradient(90deg, transparent, ${t.acc}60, transparent)`, marginBottom: 24 }}
            />
            {slide.sub && (
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
                style={{ fontSize: 'clamp(1rem,1.6vw,1.25rem)', fontWeight: 600, color: 'rgba(255,255,255,0.8)', lineHeight: 1.65, maxWidth: 560 }}>
                {slide.sub}
              </motion.p>
            )}
            {slide.say && <div style={{ textAlign: 'left', width: '100%', maxWidth: 560 }}><Say items={slide.say} /></div>}
          </motion.div>
        </div>
        <CastFigures cast={slide.cast} />
      </div>
    );
  }

  if (isCover) {
    const hasOverlay = slide.title || slide.badge || slide.coverDesc;
    return (
      <div style={{ width: '100%', height: '100%', background: '#000', position: 'relative', overflow: 'hidden' }}>
        {slide.img && (
          <motion.img
            src={slide.img} alt=""
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {hasOverlay && (
          <>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 1 }} />
            <div style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.5, delay: 0.3 }}
                style={{ padding: 'clamp(24px,4vw,44px) clamp(36px,5vw,64px)', borderRadius: 24, background: 'rgba(15,15,35,0.8)', backdropFilter: 'blur(20px)', border: '2px solid rgba(108,99,255,0.4)', boxShadow: '0 12px 48px rgba(0,0,0,0.5), 0 0 40px rgba(108,99,255,0.15)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: 520 }}>
                {slide.badge && (
                  <span style={{ display: 'inline-block', padding: '5px 18px', borderRadius: 16, background: 'linear-gradient(135deg, rgba(108,99,255,0.3), rgba(139,131,255,0.2))', border: '1.5px solid rgba(108,99,255,0.4)', fontWeight: 900, fontSize: 'clamp(0.7rem,1vw,0.82rem)', color: '#8B83FF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 14 }}>
                    {slide.badge}
                  </span>
                )}
                {slide.title && (
                  <h2 style={{ fontSize: 'clamp(1.6rem,3.5vw,2.6rem)', fontWeight: 900, color: '#fff', lineHeight: 1.15, marginBottom: slide.coverDesc ? 12 : 0 }}>
                    {slide.title}
                  </h2>
                )}
                {slide.coverDesc && (
                  <p style={{ fontSize: 'clamp(0.85rem,1.3vw,1.05rem)', fontWeight: 600, color: 'rgba(255,255,255,0.85)', lineHeight: 1.55, maxWidth: 400 }}>
                    {slide.coverDesc}
                  </p>
                )}
              </motion.div>
            </div>
          </>
        )}
      </div>
    );
  }

  const bulletStyle = slide.bulletStyle || 'rows';

  const textContent = (
    <>
      {slide.badge && (
        <motion.span initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          style={{ display: 'inline-block', padding: '6px 16px', borderRadius: 20, background: isScene ? `linear-gradient(135deg, ${BRAND.goldLight}, ${BRAND.gold})` : `linear-gradient(135deg, ${t.acc}30, ${t.acc2}20)`, border: isScene ? 'none' : `1.5px solid ${t.acc}50`, fontWeight: 900, fontSize: sc.badge, color: isScene ? BRAND.navy900 : t.acc, marginBottom: sc.gap, textTransform: 'uppercase', letterSpacing: '0.08em', backdropFilter: 'blur(8px)', boxShadow: isScene ? '0 4px 14px rgba(205,155,80,0.3)' : 'none' }}>
          {slide.badge}
        </motion.span>
      )}
      {slide.title && (
        <motion.h2 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          style={{ fontSize: sc.title, fontWeight: 900, lineHeight: 1.15, marginBottom: 6, background: isScene ? `linear-gradient(135deg, ${BRAND.ivory} 0%, ${BRAND.goldLight} 60%, ${BRAND.gold} 100%)` : `linear-gradient(135deg, #fff 0%, ${t.acc} 70%, ${t.acc2 || t.acc} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))' }}>
          {slide.title}
        </motion.h2>
      )}
      {slide.title && slide.sub && (
        <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.15, duration: 0.4 }}
          style={{ width: 50, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${t.acc}, ${t.acc2 || t.acc})`, marginBottom: sc.gap - 2 }} />
      )}
      {slide.sub && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          style={{ fontSize: sc.sub, fontWeight: 600, color: isScene ? 'rgba(249,240,231,0.92)' : 'rgba(255,255,255,0.88)', lineHeight: 1.65, marginBottom: sc.gap + 2, whiteSpace: 'pre-line', textShadow: '0 1px 6px rgba(0,0,0,0.25)' }}>
          {slide.sub}
        </motion.p>
      )}
      {slide.quote && (() => {
        const qs = slide.quoteSize;
        const qPad = qs === 'large' ? 'clamp(36px,5vw,64px) clamp(32px,4vw,56px)' : qs === 'medium' ? 'clamp(24px,3.5vw,44px) clamp(20px,3vw,36px)' : 'clamp(16px,2vw,24px)';
        const qBr = qs === 'large' ? 22 : qs === 'medium' ? 18 : 14;
        const qBg = qs === 'large' || qs === 'medium' ? 'rgba(15,15,30,0.6)' : 'rgba(15,15,30,0.5)';
        const qBl = qs === 'large' ? 6 : qs === 'medium' ? 5 : 4;
        const qSh = qs === 'large' || qs === 'medium' ? `0 8px 40px ${t.acc}25` : '0 4px 20px rgba(0,0,0,0.2)';
        const qFs = qs === 'large' ? 'clamp(1.6rem,3vw,2.4rem)' : qs === 'medium' ? 'clamp(1.25rem,2.2vw,1.8rem)' : sc.sub;
        const qFw = qs === 'large' || qs === 'medium' ? 800 : 700;
        const qLh = qs === 'large' ? 1.4 : qs === 'medium' ? 1.45 : 1.55;
        const qByFs = qs === 'large' ? 'clamp(1rem,1.5vw,1.2rem)' : qs === 'medium' ? 'clamp(0.95rem,1.3vw,1.1rem)' : sc.note;
        const qByMt = qs === 'large' ? 20 : qs === 'medium' ? 14 : 8;
        return (
        <motion.blockquote initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
          style={{ padding: qPad, borderRadius: qBr, background: qBg, borderLeft: `${qBl}px solid ${t.acc}`, marginBottom: sc.gap + 2, backdropFilter: 'blur(10px)', boxShadow: qSh }}>
          <p style={{ fontSize: qFs, fontWeight: qFw, fontStyle: 'italic', color: 'rgba(255,255,255,0.92)', lineHeight: qLh }}>"{slide.quote}"</p>
          {slide.quoteBy && <p style={{ marginTop: qByMt, fontSize: qByFs, fontWeight: 700, color: `${t.acc}` }}>— {slide.quoteBy}</p>}
        </motion.blockquote>
        );
      })()}
      {slide.bullets && bulletStyle === 'grid' && (() => {
        const compact = slide.bullets.length > 5;
        const cols = slide.bullets.length <= 4 ? 2 : compact ? 4 : 3;
        return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: compact ? 'clamp(6px,1vw,10px)' : 'clamp(10px,1.5vw,16px)', marginBottom: sc.gap, alignItems: 'stretch' }}>
          {slide.bullets.map((b, i) => (
            <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.08 * i }}
              style={{ padding: compact ? 'clamp(10px,1.2vw,14px)' : 'clamp(16px,2vw,24px)', borderRadius: compact ? 12 : 16, background: `linear-gradient(145deg, ${t.acc}22, ${t.acc2}18)`, border: `2px solid ${t.acc}35`, backdropFilter: 'blur(12px)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: compact ? 6 : 10, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', justifyContent: 'center' }}>
              <span style={{ width: compact ? 28 : 38, height: compact ? 28 : 38, borderRadius: '50%', background: `linear-gradient(135deg, ${t.acc}, ${t.acc2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: compact ? '0.78rem' : '0.95rem', color: '#fff', boxShadow: `0 4px 16px ${t.acc}40`, flexShrink: 0 }}>{i + 1}</span>
              <span style={{ fontSize: compact ? 'clamp(0.72rem,0.95vw,0.88rem)' : 'clamp(0.88rem,1.2vw,1.05rem)', fontWeight: 700, color: 'rgba(255,255,255,0.9)', lineHeight: 1.35 }}>{b}</span>
            </motion.div>
          ))}
        </div>);
      })()}
      {slide.bullets && bulletStyle === 'timeline' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: sc.gap, position: 'relative', paddingLeft: 28 }}>
          <div style={{ position: 'absolute', left: 12, top: 8, bottom: 8, width: 3, background: `linear-gradient(to bottom, ${t.acc}, ${t.acc2})`, borderRadius: 2, opacity: 0.4 }} />
          {slide.bullets.map((b, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * i }}
              style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 'clamp(12px,1.8vw,18px) clamp(14px,2vw,20px)', position: 'relative' }}>
              <span style={{ position: 'absolute', left: -22, width: 24, height: 24, borderRadius: '50%', background: t.bg, border: `3px solid ${t.acc}`, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: `linear-gradient(135deg, ${t.acc}, ${t.acc2})` }} />
              </span>
              <span style={{ fontSize: 'clamp(1rem,1.5vw,1.2rem)', fontWeight: 700, color: 'rgba(255,255,255,0.95)', lineHeight: 1.45, textShadow: '0 1px 4px rgba(0,0,0,0.2)' }}>{b}</span>
            </motion.div>
          ))}
        </div>
      )}
      {slide.bullets && bulletStyle === 'rows' && (() => {
        const lg = slide.cardSize === 'large';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: lg ? Math.max(12, sc.gap) : Math.max(6, sc.gap - 6), marginBottom: sc.gap }}>
            {slide.bullets.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * i }}
                style={{ display: 'flex', alignItems: 'flex-start', gap: lg ? 16 : 10, padding: lg ? 'clamp(16px,2.2vw,24px)' : 'clamp(8px,1.2vw,12px)', borderRadius: lg ? 16 : 10, background: t.card, border: `${lg ? 2 : 1.5}px solid ${t.cardBd}`, backdropFilter: 'blur(10px)', boxShadow: '0 2px 12px rgba(0,0,0,0.12)' }}>
                <span style={{ width: lg ? 34 : 24, height: lg ? 34 : 24, borderRadius: lg ? 10 : 7, background: `linear-gradient(135deg, ${t.acc}25, ${t.acc2}18)`, border: `1.5px solid ${t.acc}35`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: lg ? '0.9rem' : '0.72rem', color: t.acc, flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
                <span style={{ fontSize: lg ? 'clamp(1.05rem,1.6vw,1.25rem)' : sc.bullet, fontWeight: lg ? 700 : 600, color: 'rgba(255,255,255,0.95)', lineHeight: lg ? 1.5 : 1.45, textShadow: '0 1px 4px rgba(0,0,0,0.2)' }}>{b}</span>
              </motion.div>
            ))}
          </div>
        );
      })()}
      {slide.quiz && <Quiz d={slide.quiz} t={t} compact={slide.quizSize === 'compact'} fluid={isScene} onDone={onMission} />}
      {slide.cards && <Cards items={slide.cards} t={t} />}
      {slide.cia && <CIA t={t} />}
      {slide.rules && <Rules t={t} items={slide.rules} />}
      {slide.encrypt && <Encrypt t={t} />}
      {slide.scenario && <Scenario data={slide.scenario} t={t} />}
      {slide.match && <MatchGame pairs={slide.match} t={t} />}
      {slide.sorter && <DragSorter data={slide.sorter} t={t} onDone={onMission} />}
      {slide.passwordLab && <PasswordLab t={t} onDone={onMission} opts={slide.passwordLab} />}
      {slide.phone && <PhoneCleanup data={slide.phone} t={t} onDone={onMission} />}
      {slide.branch && <Branch data={slide.branch} t={t} onDone={onMission} />}
      {slide.grid && (() => {
        const n = slide.grid.length;
        const cols = slide.gridCols || (isScene ? (n <= 1 ? 1 : 2) : n <= 2 ? n : (n === 4 || n === 8) ? 2 : n <= 6 ? 3 : 4);
        const tight = isScene && n >= 5;
        const tf = tight ? 'clamp(0.84rem,1.05vw,0.95rem)' : isScene ? 'clamp(0.92rem,1.18vw,1.05rem)' : 'clamp(0.85rem,1.1vw,0.95rem)';
        const bf = tight ? 'clamp(0.78rem,0.98vw,0.9rem)' : isScene ? 'clamp(0.84rem,1.06vw,0.96rem)' : 'clamp(0.78rem,1vw,0.88rem)';
        return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: tight ? 8 : 10, alignItems: 'stretch' }}>
          {slide.grid.map((g, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 * i }}
              style={{ padding: tight ? 'clamp(9px,1.1vw,13px) clamp(11px,1.3vw,15px)' : 'clamp(12px,1.5vw,18px)', borderRadius: 14, background: isScene ? 'rgba(255,255,255,0.06)' : t.card, border: `1.5px solid ${t.cardBd}`, backdropFilter: 'blur(10px)', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontWeight: 800, fontSize: tf, color: t.acc, marginBottom: tight ? 3 : 6, lineHeight: 1.3 }}>{g.title}</p>
              <p style={{ fontWeight: 600, fontSize: bf, color: isScene ? 'rgba(249,240,231,0.9)' : 'rgba(255,255,255,0.82)', lineHeight: 1.42, flex: 1 }}>{g.text}</p>
            </motion.div>
          ))}
        </div>);
      })()}
      {slide.objectiveItems && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(6px,0.9vw,10px)', marginBottom: sc.gap }}>
          {slide.objectiveItems.map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.06, duration: 0.3 }}
              style={{ padding: 'clamp(9px,1.1vw,13px) clamp(12px,1.3vw,16px)', borderRadius: 12, background: 'rgba(255,255,255,0.07)', border: `1.5px solid ${t.acc}30`, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 'clamp(24px,2.2vw,30px)', height: 'clamp(24px,2.2vw,30px)', borderRadius: '50%', background: `linear-gradient(135deg, ${t.acc}, ${t.acc2 || t.acc})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 'clamp(0.68rem,0.85vw,0.78rem)', color: isScene ? BRAND.navy900 : '#fff', flexShrink: 0 }}>{i + 1}</span>
              <span style={{ fontWeight: 700, fontSize: isScene ? 'clamp(0.9rem,1.15vw,1.04rem)' : 'clamp(0.84rem,1.05vw,0.96rem)', color: isScene ? BRAND.ivory : '#fff', lineHeight: 1.4 }}>{item}</span>
            </motion.div>
          ))}
        </div>
      )}
      {slide.recapItems && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${slide.gridCols || 2}, 1fr)`, gap: 'clamp(6px,0.9vw,10px)', alignItems: 'stretch' }}>
          {slide.recapItems.map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05, duration: 0.3 }}
              style={{ padding: 'clamp(10px,1.2vw,14px)', borderRadius: 12, background: 'rgba(255,255,255,0.07)', border: `1.5px solid ${t.acc}30`, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontWeight: 800, fontSize: isScene ? 'clamp(0.86rem,1.08vw,0.98rem)' : 'clamp(0.8rem,1vw,0.9rem)', color: t.acc, lineHeight: 1.25 }}>{item.title}</span>
              <p style={{ fontWeight: 600, fontSize: isScene ? 'clamp(0.8rem,1vw,0.9rem)' : 'clamp(0.74rem,0.92vw,0.84rem)', color: isScene ? 'rgba(249,240,231,0.9)' : 'rgba(255,255,255,0.85)', lineHeight: 1.4, flex: 1 }}>{item.text}</p>
            </motion.div>
          ))}
        </div>
      )}
      {(slide.glossary || slide.homeTask) && (
        <div style={{ display: 'grid', gridTemplateColumns: slide.glossary && slide.homeTask ? '1fr 1fr' : '1fr', gap: 'clamp(6px,0.9vw,10px)', marginTop: 'clamp(6px,0.9vw,10px)' }}>
          {slide.glossary && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              style={{ padding: 'clamp(10px,1.2vw,14px)', borderRadius: 12, background: 'rgba(107,203,119,0.1)', border: '1.5px solid rgba(107,203,119,0.45)' }}>
              <span style={{ fontWeight: 900, fontSize: 'clamp(0.78rem,1vw,0.88rem)', color: '#9ff0aa' }}>📖 Words of the day</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {slide.glossary.map((w, i) => <span key={i} title={typeof w === 'object' ? w.def : undefined} style={{ padding: '3px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', fontWeight: 800, fontSize: 'clamp(0.74rem,0.92vw,0.84rem)', color: '#fff', cursor: typeof w === 'object' ? 'help' : 'default' }}>{typeof w === 'object' ? w.term : w}</span>)}
              </div>
            </motion.div>
          )}
          {slide.homeTask && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
              style={{ padding: 'clamp(10px,1.2vw,14px)', borderRadius: 12, background: 'rgba(77,150,255,0.1)', border: '1.5px solid rgba(77,150,255,0.45)' }}>
              <span style={{ fontWeight: 900, fontSize: 'clamp(0.78rem,1vw,0.88rem)', color: '#a3c7ff' }}>🏠 Tell it at home</span>
              <p style={{ marginTop: 6, fontWeight: 600, fontSize: 'clamp(0.78rem,0.98vw,0.9rem)', color: 'rgba(255,255,255,0.9)', lineHeight: 1.4 }}>{slide.homeTask}</p>
              {onFamilyPrint && (
                <button type="button" onClick={onFamilyPrint} style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 16, border: '1.5px solid rgba(163,199,255,0.6)', background: 'rgba(77,150,255,0.18)', color: '#dbe9ff', fontWeight: 800, fontSize: 'clamp(0.7rem,0.86vw,0.8rem)', cursor: 'pointer' }}>
                  🖨️ Print the family page
                </button>
              )}
            </motion.div>
          )}
        </div>
      )}
      {slide.note && <p style={{ marginTop: sc.gap, fontSize: sc.note, fontWeight: 600, color: `${t.acc}`, fontStyle: 'italic', opacity: 0.8 }}>{slide.note}</p>}
      {slide.say && <div style={{ textAlign: 'left' }}><Say items={slide.say} compact={isScene || density === 'heavy'} /></div>}
    </>
  );

  const bgLayer = slide.bgImg ? (
    <>
      <img src={slide.bgImg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 0 }} />
    </>
  ) : null;

  if (slide.layout === 'recap') {
    const items = slide.recapItems || [];
    const hasCast = !!(slide.cast && slide.cast.length);
    const cols = hasCast ? 2 : items.length <= 2 ? items.length : (items.length === 4 || items.length === 8) ? 2 : items.length <= 6 ? 3 : 4;
    const hasExtras = !!(slide.glossary || slide.homeTask);
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: slide.bgImg ? 'transparent' : t.bg, position: 'relative' }}>
        {bgLayer}
        <div style={{ padding: sc.pad, display: 'flex', flexDirection: 'column', alignItems: hasCast ? 'flex-start' : 'center', justifyContent: 'center', height: '100%', position: 'relative', zIndex: 1, maxWidth: hasCast ? undefined : 800, width: hasCast ? `${slide.textWidth || 66}%` : undefined, margin: hasCast ? 0 : '0 auto', boxSizing: 'border-box', gap: 8, textAlign: hasCast ? 'left' : 'center', overflow: 'auto' }}>
          <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            style={{ fontSize: sc.title, fontWeight: 900, lineHeight: 1.15, marginBottom: 4, background: `linear-gradient(135deg, #fff 0%, ${t.acc} 70%, ${t.acc2 || t.acc} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' }}>
            {slide.title}
          </motion.h2>
          {slide.sub && <p style={{ fontSize: 'clamp(0.85rem,1.15vw,1rem)', fontWeight: 600, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5, marginBottom: 2 }}>{slide.sub}</p>}
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.15, duration: 0.4 }}
            style={{ width: 50, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${t.acc}, ${t.acc2 || t.acc})`, marginBottom: 6 }} />
          <div style={{ width: '100%', display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 'clamp(6px,1vw,10px)', alignItems: 'stretch' }}>
            {items.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05, duration: 0.3 }}
                style={{ padding: 'clamp(10px,1.2vw,14px)', borderRadius: 12, background: 'rgba(0,0,0,0.18)', border: `1.5px solid ${t.acc}20`, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontWeight: 800, fontSize: 'clamp(0.78rem,1vw,0.88rem)', color: t.acc, lineHeight: 1.25 }}>{item.title}</span>
                <p style={{ fontWeight: 600, fontSize: 'clamp(0.72rem,0.9vw,0.82rem)', color: 'rgba(255,255,255,0.75)', lineHeight: 1.4, flex: 1 }}>{item.text}</p>
              </motion.div>
            ))}
          </div>
          {hasExtras && (
            <div style={{ width: '100%', display: 'grid', gridTemplateColumns: slide.glossary && slide.homeTask ? '1fr 1fr' : '1fr', gap: 'clamp(6px,1vw,10px)', marginTop: 2 }}>
              {slide.glossary && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                  style={{ padding: 'clamp(10px,1.2vw,14px)', borderRadius: 12, background: 'rgba(107,203,119,0.08)', border: '1.5px solid rgba(107,203,119,0.4)' }}>
                  <span style={{ fontWeight: 900, fontSize: 'clamp(0.78rem,1vw,0.88rem)', color: '#8ee39a' }}>📖 Words of the day</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {slide.glossary.map((w, i) => <span key={i} title={typeof w === 'object' ? w.def : undefined} style={{ padding: '3px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', fontWeight: 800, fontSize: 'clamp(0.7rem,0.9vw,0.8rem)', color: '#fff' }}>{typeof w === 'object' ? w.term : w}</span>)}
                  </div>
                </motion.div>
              )}
              {slide.homeTask && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
                  style={{ padding: 'clamp(10px,1.2vw,14px)', borderRadius: 12, background: 'rgba(77,150,255,0.08)', border: '1.5px solid rgba(77,150,255,0.4)' }}>
                  <span style={{ fontWeight: 900, fontSize: 'clamp(0.78rem,1vw,0.88rem)', color: '#8fbaff' }}>🏠 Tell it at home</span>
                  <p style={{ marginTop: 6, fontWeight: 600, fontSize: 'clamp(0.72rem,0.9vw,0.82rem)', color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}>{slide.homeTask}</p>
                </motion.div>
              )}
            </div>
          )}
        </div>
        <CastFigures cast={slide.cast} />
      </div>
    );
  }

  if (slide.layout === 'objectives') {
    const items = slide.objectiveItems || [];
    const cols = items.length <= 2 ? items.length : (items.length === 4 || items.length === 8) ? 2 : items.length <= 6 ? 3 : 4;
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: slide.bgImg ? 'transparent' : t.bg, position: 'relative' }}>
        {bgLayer}
        <div style={{ padding: sc.pad, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', position: 'relative', zIndex: 1, maxWidth: 800, margin: '0 auto', boxSizing: 'border-box', gap: 8 }}>
          <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            style={{ fontSize: sc.title, fontWeight: 900, lineHeight: 1.15, marginBottom: 4, background: `linear-gradient(135deg, #fff 0%, ${t.acc} 70%, ${t.acc2 || t.acc} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' }}>
            {slide.title}
          </motion.h2>
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.15, duration: 0.4 }}
            style={{ width: 50, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${t.acc}, ${t.acc2 || t.acc})`, marginBottom: 6 }} />
          <div style={{ width: '100%', display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 'clamp(6px,1vw,10px)', alignItems: 'stretch' }}>
            {items.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.06, duration: 0.3 }}
                style={{ padding: 'clamp(10px,1.2vw,14px)', borderRadius: 12, background: 'rgba(0,0,0,0.18)', border: `1.5px solid ${t.acc}20`, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 'clamp(24px,2.2vw,30px)', height: 'clamp(24px,2.2vw,30px)', borderRadius: '50%', background: `linear-gradient(135deg, ${t.acc}, ${t.acc2 || t.acc})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 'clamp(0.68rem,0.85vw,0.78rem)', color: '#fff', flexShrink: 0 }}>{i + 1}</span>
                <span style={{ fontWeight: 700, fontSize: 'clamp(0.78rem,1vw,0.9rem)', color: 'rgba(255,255,255,0.9)', lineHeight: 1.35 }}>{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Scene layout: big illustration left, readable dark text panel right ───
  if (isScene) {
    if (isChapterCover && slide.coverStyle && cover && slide.img) {
      return <ChapterCover slide={slide} meta={cover} t={t} />;
    }
    // Default: the picture fills the whole slide and a navy shade keeps the text panel readable
    // ('full'). sceneFit 'fit' / 'cover' keep the older picture-left + solid-panel-right layout.
    // % of width taken by the illustration; the text panel takes the rest.
    // Phones (portrait): always full-bleed picture on top + text panel at the bottom.
    const full = narrow || (slide.sceneFit !== 'fit' && slide.sceneFit !== 'cover');
    const bottom = full && (narrow || slide.textPos === 'bottom');
    const imgW = full ? (slide.sceneImgWidth || (isChapterCover ? 52 : 46)) : (slide.sceneImgWidth || (isChapterCover ? 54 : 44));
    const pc = panelColors(sceneTint, slide.panelHsl);
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: pc.solid, position: 'relative', transition: 'background 0.6s' }}>
        {full && slide.img && <FullBleed src={slide.img} imgPos={narrow ? (slide.imgPos || 'center top') : slide.imgPos} pos={bottom ? 'bottom' : slide.textPos} panelW={100 - imgW} strong={!isChapterCover} />}
        {/* the illustration, blurred and stretched, fills the whole slide so the panel is "made of" the picture */}
        {!full && slide.img && <img src={slide.img} alt="" aria-hidden style={{ position: 'absolute', inset: '-6%', width: '112%', height: '112%', objectFit: 'cover', zIndex: 0, filter: 'blur(28px) saturate(1.15)', opacity: 0.55 }} />}
        {!full && slide.img && (() => {
          // 'fit' (default): the picture is reduced to ~72% of the height so almost the whole scene is visible,
          // the blurred copy behind fills the rest. 'cover': full-bleed crop (legacy / close-up portraits).
          const fit = (slide.sceneFit || 'fit') !== 'cover';
          const hMask = 'linear-gradient(90deg, #000 80%, transparent 100%)';
          const vMask = fit ? 'linear-gradient(180deg, transparent 0, #000 7%, #000 93%, transparent 100%)' : 'none';
          return (
            <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
              style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${imgW + 6}%`, zIndex: 1, display: 'flex', alignItems: 'center', WebkitMaskImage: hMask, maskImage: hMask }}>
              <img src={slide.img} alt="" style={{ width: '100%', height: fit ? '72%' : '100%', objectFit: 'cover', objectPosition: slide.imgPos || 'center', WebkitMaskImage: vMask, maskImage: vMask }} />
            </motion.div>
          );
        })()}
        {/* panel: starts with the picture's hue next to the image, settles into C.O.R.E. navy */}
        {!full && <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: `${100 - imgW}%`, zIndex: 2, transition: 'background 0.6s', background: pc.gradient }} />}
        <FitPanel
          style={bottom
            ? { position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: narrow ? '68%' : '100%', zIndex: 3, padding: narrow ? '0 18px 22px' : '0 clamp(28px,4vw,64px) clamp(26px,3.4vw,48px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: narrow ? 'auto' : undefined }
            : { position: 'absolute', top: 0, right: 0, bottom: 0, width: `${100 - imgW}%`, zIndex: 3, padding: 'clamp(16px,2.4vw,32px) clamp(22px,2.8vw,44px) clamp(22px,2.8vw,38px) clamp(28px,4vw,64px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'auto' }}
          innerStyle={{ textAlign: 'left', width: bottom ? 'min(100%, 760px)' : '100%', margin: 'auto 0', ...(isChapterCover ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' } : null) }}>
          {textContent}
        </FitPanel>
        <CastFigures cast={slide.cast} />
      </div>
    );
  }

  if (isSplit && slide.img) {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: slide.bgImg ? 'transparent' : t.bg, position: 'relative' }}>
        {slide.bgImg ? bgLayer : (
          <>
            <div style={{ position: 'absolute', top: '-8%', right: '-5%', width: '30%', height: '30%', borderRadius: '50%', background: `${t.acc}08`, filter: 'blur(60px)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-10%', left: '-8%', width: '25%', height: '25%', borderRadius: '50%', background: `${t.acc2}06`, filter: 'blur(50px)', pointerEvents: 'none' }} />
          </>
        )}
        <div style={{ padding: `clamp(20px, 3vw, 40px) ${sc.pad}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', position: 'relative', zIndex: 1, boxSizing: 'border-box', overflow: 'auto' }}>
          <div style={{ width: '100%', maxWidth: 820, textAlign: 'center', flexShrink: 0 }}>
            {textContent}
          </div>
        </div>
        <div
          onMouseEnter={() => setImgHover(true)}
          onMouseLeave={() => setImgHover(false)}
          style={{ position: 'absolute', bottom: 0, right: 0, padding: 30, zIndex: 10, cursor: 'pointer' }}>
          <div style={{ borderRadius: 20, overflow: 'hidden', border: `2px solid ${t.acc}40`, boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 20px ${t.acc}15` }}>
            <img src={slide.img} alt=""
              style={{ display: 'block', borderRadius: 20, width: imgHover ? 'min(55vw, 55vh)' : 100, height: 'auto', transition: 'width 0.35s ease' }} />
          </div>
        </div>
      </div>
    );
  }

  if (isSplit && !slide.img) {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: slide.bgImg ? 'transparent' : t.bg, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        {slide.bgImg ? bgLayer : (
          <>
            <div style={{ position: 'absolute', top: '-8%', left: '-10%', width: '40%', height: '40%', borderRadius: '50%', background: `${t.acc}08`, filter: 'blur(50px)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-10%', right: '-5%', width: '30%', height: '30%', borderRadius: '50%', background: `${t.acc2}06`, filter: 'blur(40px)', pointerEvents: 'none' }} />
          </>
        )}
        <div className={isChalkBg ? 'chalk-font' : undefined} style={{ padding: isChalkBg ? CHALK_PAD : sc.pad, position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: isChalkBg ? 'flex-start' : 'center', maxWidth: isChalkBg ? undefined : 800, boxSizing: 'border-box', height: '100%', overflow: 'hidden' }}>
          {textContent}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: slide.bgImg ? 'transparent' : t.bg, position: 'relative' }}>
      {slide.bgImg ? bgLayer : (
        <>
          <div style={{ position: 'absolute', top: '-8%', right: '-5%', width: '30%', height: '30%', borderRadius: '50%', background: `${t.acc}08`, filter: 'blur(60px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-10%', left: '-8%', width: '25%', height: '25%', borderRadius: '50%', background: `${t.acc2}06`, filter: 'blur(50px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: '40%', right: '10%', width: '15%', height: '15%', borderRadius: '50%', background: `${t.glow}`, filter: 'blur(40px)', pointerEvents: 'none' }} />
        </>
      )}
      {slide.cast && slide.cast.length ? (
        <>
          {/* text column kept inside the board frame of lesson-end.svg; translucent panel hides the doodles behind the text */}
          <div style={isBoardBg
            ? { position: 'absolute', top: '15%', bottom: '10%', left: '10%', width: `${slide.textWidth || 60}%`, zIndex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'left', boxSizing: 'border-box', padding: 'clamp(14px,1.6vw,24px)', background: 'rgba(6,14,10,0.55)', borderRadius: 18, border: '1px solid rgba(255,255,255,0.08)', overflow: 'auto' }
            : { padding: sc.pad, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'left', height: '100%', position: 'relative', zIndex: 1, width: `${slide.textWidth || 62}%`, boxSizing: 'border-box', overflow: 'auto' }}>
            {textContent}
          </div>
          <CastFigures cast={slide.cast} />
        </>
      ) : (
        <div style={{ padding: sc.pad, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', height: '100%', position: 'relative', zIndex: 1, maxWidth: 800, margin: '0 auto', boxSizing: 'border-box' }}>
          {textContent}
        </div>
      )}
    </div>
  );
}

function Portal({ children }) {
  const el = useMemo(() => { const d = document.createElement('div'); d.style.cssText = 'position:fixed;inset:0;z-index:99999'; return d; }, []);
  useEffect(() => { document.body.appendChild(el); return () => document.body.removeChild(el); }, [el]);
  return ReactDOM.createPortal(children, el);
}

export default function InteractiveLesson({ slides, title, description, duration, onBack, backLabel = 'Back to Lessons', onComplete, onAward, onFamilyPrint, earnedBadges }) {
  const [started, setStarted] = useState(false);
  const [cur, setCur] = useState(0);
  const [far, setFar] = useState(0);
  const [hoverNav, setHoverNav] = useState(null);
  const [toast, setToast] = useState(null);
  const [earned, setEarned] = useState([]);
  const total = slides.length;
  const completedRef = useRef(false);
  const awardedRef = useRef(new Set((earnedBadges || []).map(b => b.id)));
  // Lessons built with the "scene" layout use the C.O.R.E. brand theme; older HTML-era decks keep their legacy look.
  const isCore = useMemo(() => slides.some(s => s.layout === 'scene'), [slides]);

  useEffect(() => { if (cur > far) setFar(cur); }, [cur, far]);

  const giveAward = useCallback((award) => {
    if (!award || !award.id) return;
    if (awardedRef.current.has(award.id)) return;
    awardedRef.current.add(award.id);
    setEarned(list => [...list, award]);
    setToast(award);
    if (typeof onAward === 'function') onAward(award);
  }, [onAward]);

  // Awards attached to a slide are given on arrival, unless the slide hosts a mission: then they are given when the mission is completed.
  useEffect(() => {
    const s = slides[cur];
    if (!s || !s.award) return;
    const hasMission = s.sorter || s.passwordLab || s.phone || s.branch || s.quiz;
    if (!hasMission) giveAward(s.award);
  }, [cur, slides, giveAward]);

  const onMission = useCallback(() => {
    const s = slides[cur];
    if (s && s.award) giveAward(s.award);
  }, [cur, slides, giveAward]);

  useEffect(() => () => { if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel(); }, [cur]);

  useEffect(() => {
    if (completedRef.current) return;
    if (typeof onComplete !== 'function') return;
    if (total > 0 && far >= total - 1) {
      completedRef.current = true;
      onComplete();
    }
  }, [far, total, onComplete]);

  const onKey = useCallback(e => {
    if (!started) return;
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    if (e.key === 'ArrowRight') { e.preventDefault(); setCur(c => Math.min(c + 1, total - 1)); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); setCur(c => Math.max(c - 1, 0)); }
    if (e.key === 'Escape') setStarted(false);
  }, [total, started]);

  useEffect(() => { window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey); }, [onKey]);
  useEffect(() => { if (started) document.body.style.overflow = 'hidden'; else document.body.style.overflow = ''; return () => { document.body.style.overflow = ''; }; }, [started]);

  const pct = total > 1 ? ((cur + 1) / total) * 100 : 100;
  const sl0 = slides[cur];
  const allEarned = useMemo(() => {
    const seen = new Set(); const out = [];
    [...(earnedBadges || []), ...earned].forEach(b => { if (b && !seen.has(b.id)) { seen.add(b.id); out.push(b); } });
    return out;
  }, [earnedBadges, earned]);
  const sl = sl0 && sl0.layout === 'thankyou' && allEarned.length ? { ...sl0, badgesEarned: allEarned } : sl0;
  const theme = isCore ? THEME_CORE : (THEMES_GOLD[sl?.ch] || THEMES_GOLD.intro);
  const chrome = isCore
    ? { bg: BRAND.navy900, bar: 'rgba(7,20,45,0.97)', barBd: 'rgba(226,184,120,0.18)', text: 'rgba(249,240,231,0.6)', chap: BRAND.goldLight, track: 'rgba(226,184,120,0.14)', fill: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldLight})`, glow: 'rgba(226,184,120,0.45)', exitBg: 'rgba(226,184,120,0.1)', exitBd: 'rgba(226,184,120,0.3)', font: BRAND.font }
    : { bg: '#1A1A2E', bar: 'rgba(26,26,46,0.97)', barBd: 'rgba(108,99,255,0.1)', text: 'rgba(255,255,255,0.45)', chap: '#8B83FF', track: 'rgba(108,99,255,0.1)', fill: 'linear-gradient(90deg, #6C63FF, #8B83FF)', glow: 'rgba(108,99,255,0.4)', exitBg: 'rgba(108,99,255,0.1)', exitBd: 'rgba(108,99,255,0.2)', font: undefined };

  const chapters = useMemo(() => { const r = []; let last = null; slides.forEach((s, i) => { if (s.chTitle && s.chTitle !== last) { r.push({ t: s.chTitle, i }); last = s.chTitle; } }); return r; }, [slides]);
  const chName = useMemo(() => { let n = ''; for (const c of chapters) { if (c.i <= cur) n = c.t; } return n; }, [chapters, cur]);
  // Chapter-cover metadata (numbering, the stops map, the chapter guide and the badge at stake)
  const coverMeta = useMemo(() => {
    const covers = slides.map((s, i) => ({ s, i })).filter(({ s }) => isCoverSlide(s) && /^capture/i.test(s.ch || ''));
    const idx = covers.findIndex(c => c.i === cur);
    if (idx < 0) return null;
    const s = slides[cur];
    const inCh = slides.filter(x => x.ch === s.ch);
    const award = (inCh.find(x => x.award) || {}).award || null;
    const missions = inCh.filter(x => x.sorter || x.branch || x.phone || x.passwordLab).length;
    const quizzes = inCh.filter(x => x.quiz).length;
    return { n: idx + 1, total: covers.length, stops: covers.map(c => c.s.title), award, missions, quizzes };
  }, [slides, cur]);

  const navZone = (dir) => (
    <div
      onMouseEnter={() => setHoverNav(dir)}
      onMouseLeave={() => setHoverNav(null)}
      style={{ position: 'absolute', [dir === 'l' ? 'left' : 'right']: 0, top: 0, width: 80, height: '100%', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <button type="button"
        onClick={() => setCur(c => dir === 'l' ? Math.max(c - 1, 0) : Math.min(c + 1, total - 1))}
        style={{ width: 48, height: 48, borderRadius: '50%', background: hoverNav === dir ? 'rgba(26,26,46,0.7)' : 'transparent', border: `2px solid ${hoverNav === dir ? 'rgba(108,99,255,0.2)' : 'transparent'}`, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: hoverNav === dir ? 'blur(8px)' : 'none', boxShadow: hoverNav === dir ? '0 4px 16px rgba(0,0,0,0.3)' : 'none', transition: 'all 0.25s', opacity: hoverNav === dir ? 1 : 0 }}
        onMouseEnter={e => { if (hoverNav === dir) { e.currentTarget.style.background = 'rgba(108,99,255,0.25)'; e.currentTarget.style.borderColor = 'rgba(108,99,255,0.5)'; } }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(26,26,46,0.7)'; e.currentTarget.style.borderColor = 'rgba(108,99,255,0.2)'; }}>
        {dir === 'l' ? <FiChevronLeft size={22} /> : <FiChevronRight size={22} />}
      </button>
    </div>
  );

  // Touch devices: swipe left/right changes slide. Ignored when the gesture starts on a control or on a draggable
  // widget (sorter chips set touch-action: none), so missions keep working.
  const swipeRef = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches[0]; if (!t) return;
    let blocked = !!(e.target.closest && e.target.closest('input,textarea,select,button,a,video,[data-noswipe]'));
    for (let n = e.target; !blocked && n && n !== e.currentTarget; n = n.parentElement) { if (n.style && n.style.touchAction === 'none') blocked = true; }
    swipeRef.current = blocked ? null : { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    const s = swipeRef.current; swipeRef.current = null;
    const t = e.changedTouches[0]; if (!s || !t) return;
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Date.now() - s.t > 800 || Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (dx < 0) setCur(c => Math.min(c + 1, total - 1)); else setCur(c => Math.max(c - 1, 0));
  };

  const slideArea = (
    <div style={{ position: 'relative', width: '100%', height: '100%' }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onTouchCancel={() => { swipeRef.current = null; }}>
      <AnimatePresence mode="wait">
        <motion.div key={cur} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} style={{ position: 'absolute', inset: 0 }}>
          <Slide slide={sl} t={theme} num={cur + 1} total={total} onBack={onBack} backLabel={backLabel} onMission={onMission} onFamilyPrint={onFamilyPrint} cover={coverMeta} />
        </motion.div>
      </AnimatePresence>
      {cur > 0 && navZone('l')}
      {cur < total - 1 && navZone('r')}
      <AnimatePresence>{toast && <BadgeToast key={toast.id} award={toast} onClose={() => setToast(null)} />}</AnimatePresence>
    </div>
  );

  if (!started) {
    const introBg = isCore ? '/brand/core-classroom.jpg' : '/illustrations/lesson-start-classroom.png';
    return (
      <Portal>
        <div style={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden', fontFamily: chrome.font, background: chrome.bg }}>
          <img src={introBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', inset: 0, background: isCore ? 'linear-gradient(180deg, rgba(7,20,45,0.35) 0%, rgba(7,20,45,0.55) 55%, rgba(7,20,45,0.85) 100%)' : 'rgba(0,0,0,0.25)' }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 18, padding: 24, textAlign: 'center' }}>
            {isCore && (
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <img src={BRAND.owl} alt="C.O.R.E." style={{ width: 'clamp(72px,8vw,110px)', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.5))' }} />
                <span style={{ fontWeight: 800, letterSpacing: '0.28em', fontSize: 'clamp(0.7rem,0.9vw,0.82rem)', color: BRAND.goldLight, textTransform: 'uppercase' }}>C.O.R.E. for Schools</span>
                <h1 style={{ fontSize: 'clamp(1.7rem,3.6vw,2.8rem)', fontWeight: 900, lineHeight: 1.1, maxWidth: 820, background: `linear-gradient(135deg, ${BRAND.ivory} 0%, ${BRAND.goldLight} 60%, ${BRAND.gold} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.45))' }}>{title}</h1>
                {description && <p style={{ maxWidth: 640, fontWeight: 600, fontSize: 'clamp(0.9rem,1.2vw,1.05rem)', color: 'rgba(249,240,231,0.85)', lineHeight: 1.55, textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>{description}</p>}
                <span style={{ fontWeight: 700, fontSize: 'clamp(0.74rem,0.9vw,0.84rem)', color: 'rgba(249,240,231,0.6)' }}>{total} slides</span>
              </motion.div>
            )}
            <motion.button type="button"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              onClick={() => setStarted(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '18px 52px', borderRadius: 32, background: isCore ? `linear-gradient(135deg, ${BRAND.goldLight}, ${BRAND.gold})` : 'linear-gradient(135deg, #6C63FF, #8B83FF)', color: isCore ? BRAND.navy900 : '#fff', fontWeight: 900, fontSize: 'clamp(1.1rem,1.8vw,1.35rem)', border: 'none', cursor: 'pointer', boxShadow: isCore ? '0 8px 40px rgba(205,155,80,0.5)' : '0 8px 40px rgba(108,99,255,0.6)', letterSpacing: '0.03em', transition: 'transform 0.2s, box-shadow 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
              Start the Lesson <FiArrowRight size={22} />
            </motion.button>
          </div>
          {onBack && (
            <motion.button type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={onBack}
              style={{ position: 'absolute', top: 20, left: 20, zIndex: 2, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 12, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', border: '1.5px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.6)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.4)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}>
              <FiChevronLeft size={16} /> {backLabel}
            </motion.button>
          )}
        </div>
      </Portal>
    );
  }

  return (
    <Portal>
      <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: chrome.bg, fontFamily: chrome.font }}>
        <style>{`@media (max-width: 640px){
          .core-il-wordmark{ display:none !important; }
          .core-il-title{ flex: 0 0 auto !important; }
          .core-il-title > span:last-child{ display:none !important; }
          .core-il-mid{ flex: 1 1 auto !important; min-width: 0 !important; justify-content: flex-start; gap: 8px !important; }
          .core-il-bar{ padding-left: 12px !important; padding-right: 12px !important; }
        }`}</style>
        <div className="core-il-bar" style={{ flexShrink: 0, background: chrome.bar, borderBottom: `1px solid ${chrome.barBd}`, backdropFilter: 'blur(12px)', padding: '6px 18px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
            <span className="core-il-title" style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 800, fontSize: '0.82rem', color: chrome.text, minWidth: 0, flex: '1 1 auto' }}>
              {isCore && <img src={BRAND.owl} alt="" style={{ width: 22, height: 22, flexShrink: 0 }} />}
              {isCore && <span className="core-il-wordmark" style={{ color: BRAND.goldLight, letterSpacing: '0.18em', fontSize: '0.74rem', flexShrink: 0 }}>C.O.R.E.</span>}
              {isCore && <span className="core-il-wordmark" style={{ width: 1, height: 14, background: chrome.barBd, flexShrink: 0 }} />}
              <span style={{ minWidth: 0, flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
            </span>
            <div className="core-il-mid" style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <span style={{ fontWeight: 800, fontSize: '0.78rem', color: chrome.text, flexShrink: 0 }}>{cur + 1}/{total}</span>
              {chName && <span style={{ fontWeight: 700, fontSize: '0.78rem', color: chrome.chap, opacity: 0.9, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chName}</span>}
              {isCore && allEarned.length > 0 && (
                <span title={allEarned.map(b => b.name).join(' · ')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, padding: '2px 8px', borderRadius: 999, background: 'rgba(226,184,120,0.12)', border: '1px solid rgba(226,184,120,0.35)', fontSize: '0.72rem', fontWeight: 800, color: BRAND.goldLight }}>
                  {allEarned.slice(-4).map((b, i) => <span key={i} style={{ fontSize: 13 }}>{b.icon}</span>)} {allEarned.length}
                </span>
              )}
            </div>
            <motion.button type="button" whileHover={{ scale: 1.05, background: 'rgba(255,101,132,0.2)' }} whileTap={{ scale: 0.95 }}
              onClick={() => setStarted(false)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 14px', borderRadius: 8, background: chrome.exitBg, border: `1.5px solid ${chrome.exitBd}`, color: '#fff', fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>
              <FiX size={15} /> Exit
            </motion.button>
          </div>
          <div style={{ height: 3, borderRadius: 2, background: chrome.track }}>
            <motion.div animate={{ width: `${pct}%` }} transition={{ duration: 0.3 }} style={{ height: '100%', borderRadius: 2, background: chrome.fill, boxShadow: `0 0 8px ${chrome.glow}` }} />
          </div>
        </div>
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>{slideArea}</div>
        <RotatePrompt />
      </div>
    </Portal>
  );
}
